import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReportsLayoutContext } from '../../components/reports/Layout';
import { useUserAccess } from '../../hooks/useUserAccess';
import axios from 'axios';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { DataGrid, DataGridCommandFilter } from '@t-one-internal/design-system/lib/components/ui/data-grid';
import { DataGridColumnHeader } from '@t-one-internal/design-system/lib/components/ui/data-grid/data-grid-column-header';
import type { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  DatePicker,
  Card,
  CardContent,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@t-one-internal/design-system';
import {
  MagnifyingGlass,
  DownloadSimple,
  ArrowLeft,
  ArrowRight,
  Target,
  ChartBar,
  Table,
  UsersThree,
  ChartLineUp,
  ChartPie,
  ListMagnifyingGlass,
  ShieldCheck,
  Megaphone,
  CalendarCheck,
  SquaresFour,
  List,
  User,
  Buildings,
  Star,
  TrendUp,
  BookOpen,
  GraduationCap,
  Certificate,
  Clock,
  Path,
  Chats,
  PuzzlePiece,
  VideoCamera,
  ClipboardText,
  Exam,
  Eye,
  Timer,
} from '@phosphor-icons/react';
import {
  fetchCycleSummary,
  fetchCycleUsers,
  fetchGoalProgressDashboard,
  fetchGoalCompletionRate,
  fetchIndividualGoalSummary,
  fetchGoalSettingCompliance,
  fetchReviewCycleExtended,
  fetchSurveyParticipation,
  fetchCheckinFrequency,
  fetchReportData,
  buildQueryConfig,
} from '../../api/reportsApi';
import analyticsApiClient from '../../api/analyticsApiClient';
import { customReportsApi } from '../../api/customReportsApi';
import { useExportReport } from '../../hooks/useExportReport';
import Scheduler from '../../components/reports/custom-builder/components/Scheduler';
import type { Schedule } from '../../components/reports/custom-builder/types';
import { transformScheduleToApi, transformRecipientsToApi } from '../../api/customReportsApi';
import type {
  ColumnInfo,
  TimeRange,
  ExportOptions,
} from '../../types/reportsApi';

// Helper: creates a sortable header using DataGridColumnHeader
const makeSortableHeader = (title: string) => ({ column, header, table }: any) => (
  <DataGridColumnHeader column={column} header={header} table={table} title={title} />
);

// Column alias mapping for Cycles: API field name -> Display name
const CYCLE_COLUMN_ALIASES: Record<string, string> = {
  id: 'ID',
  cy_title: 'Cycle Name',
  cy_type: 'Type',
  total_reviews: 'Reviews',
  assigned_count: 'Assigned',
  in_progress_count: 'In Progress',
  completed_count: 'Completed',
  cy_created_on: 'Created',
  cy_stdate: 'Start Date',
  cy_endate: 'End Date',
  org_id: 'Org ID',
  status: 'Status',
};

// Column alias mapping for Users: API field name -> Display name
const USER_COLUMN_ALIASES: Record<string, string> = {
  user_display_id: 'User ID',
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  review_count: 'Review',
  assigned_count: 'Assigned',
  in_progress_count: 'In Progress',
  completed_count: 'Completed',
  primary_unit_name: 'Unit',
  manager_name: 'Manager',
};

// Columns to display for Cycles (in order)
const CYCLE_DISPLAY_COLUMNS = [
  'cy_title',
  'cy_type',
  'total_reviews',
  'assigned_count',
  'in_progress_count',
  'completed_count',
  'cy_created_on',
];

// Columns to display for Users (in order)
const USER_DISPLAY_COLUMNS = [
  'user_display_id',
  'first_name',
  'last_name',
  'email',
  'review_count',
  'assigned_count',
  'in_progress_count',
  'completed_count',
];

// Sub-view types (used internally by fetch functions)
type GoalSubView = 'progress_dashboard' | 'completion_rate' | 'goal_details' | 'goal_setting_compliance';
type CycleSubView = 'cycle_summary' | 'review_cycle_extended';
type EngagementSubView = 'survey_participation' | 'checkin_frequency';

// All report keys (hardcoded on-demand reports)
type PSReportKey =
  | CycleSubView
  | 'cycle_users'
  | GoalSubView
  | EngagementSubView;

// Learn report keys match the backend registry keys
type LearnReportKey =
  // Courses (6)
  | 'learn_course_catalog' | 'learn_course_category' | 'learn_course_components'
  | 'learn_new_courses' | 'learn_trainer_courses' | 'learn_course_workrole'
  // Enrollments (6)
  | 'learn_enrollment_summary' | 'learn_enrollment_assignment' | 'learn_mandatory_optional'
  | 'learn_overdue_enrollments' | 'learn_top_courses' | 'learn_learner_enrollment'
  // ILT & Sessions (6)
  | 'learn_event_summary' | 'learn_session_schedule' | 'learn_session_enrollment'
  | 'learn_attendance_report' | 'learn_no_show_report' | 'learn_trainer_utilization'
  // Learning Paths (3)
  | 'learn_lp_overview' | 'learn_lp_progress' | 'learn_lp_completion'
  // Compliance (3)
  | 'learn_training_compliance' | 'learn_completion_rate' | 'learn_time_to_complete'
  // Coaching (4)
  | 'learn_coaching_summary' | 'learn_coach_workload' | 'learn_coaching_sessions' | 'learn_coaching_assessment'
  // SCORM & Content (3)
  | 'learn_scorm_activity' | 'learn_scorm_attempts' | 'learn_content_usage'
  // Users & Department (2)
  | 'learn_user_summary' | 'learn_department_summary'
  // Bundles (1)
  | 'learn_bundle_summary'
  // Policyholder (2)
  | 'learn_policyholder_overview' | 'learn_policyholder_enrollments';

type ReportKey = PSReportKey | LearnReportKey;

// Report card configuration
interface ReportCard {
  key: string;
  title: string;
  description: string;
  category: string;
  icon: typeof ChartBar;
  iconBg: string;
  iconColor: string;
  gradient: string;
  badgeBg: string;
  badgeText: string;
  isDynamic?: boolean; // true for API-fetched report types
  metricKey?: string; // metric_key for dynamic reports
  reportTypeId?: string; // API id for dynamic reports
  requiredPermission?: string; // entity path for view permission check (e.g., 'learn.lms.assignments')
}

const CYCLES_STYLE = { iconBg: 'bg-blue-50', iconColor: 'text-blue-600', gradient: 'from-blue-500 to-blue-600', badgeBg: 'bg-blue-50', badgeText: 'text-blue-700' };
const USERS_STYLE = { iconBg: 'bg-violet-50', iconColor: 'text-violet-600', gradient: 'from-violet-500 to-violet-600', badgeBg: 'bg-violet-50', badgeText: 'text-violet-700' };
const GOALS_STYLE = { iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', gradient: 'from-emerald-500 to-emerald-600', badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700' };
const ENGAGEMENT_STYLE = { iconBg: 'bg-amber-50', iconColor: 'text-amber-600', gradient: 'from-amber-500 to-amber-600', badgeBg: 'bg-amber-50', badgeText: 'text-amber-700' };

const REPORT_CARDS: ReportCard[] = [
  { key: 'cycle_summary', title: 'Cycle Summary', description: 'Overview of review cycles with status and progress', category: 'Cycles', icon: ChartBar, ...CYCLES_STYLE },
  { key: 'review_cycle_extended', title: 'Extended View', description: 'Detailed review cycle metrics with completion rates', category: 'Cycles', icon: Table, ...CYCLES_STYLE },
  { key: 'cycle_users', title: 'Users Summary', description: 'User participation across review cycles', category: 'Users', icon: UsersThree, ...USERS_STYLE },
  { key: 'progress_dashboard', title: 'Progress Dashboard', description: 'Goal progress overview by cycle and framework', category: 'Goals', icon: ChartLineUp, ...GOALS_STYLE },
  { key: 'completion_rate', title: 'Completion Rate', description: 'Individual goal completion percentages by employee', category: 'Goals', icon: ChartPie, ...GOALS_STYLE },
  { key: 'goal_details', title: 'Goal Details', description: 'Individual goal breakdown with status and priority', category: 'Goals', icon: ListMagnifyingGlass, ...GOALS_STYLE },
  { key: 'goal_setting_compliance', title: 'Goal Setting Compliance', description: 'Employee compliance with goal setting requirements', category: 'Goals', icon: ShieldCheck, ...GOALS_STYLE },
  { key: 'survey_participation', title: 'Survey Participation', description: 'Survey response rates and participation metrics', category: 'Engagement', icon: Megaphone, ...ENGAGEMENT_STYLE },
  { key: 'checkin_frequency', title: 'Check-in Frequency', description: 'Employee check-in frequency across review cycles', category: 'Engagement', icon: CalendarCheck, ...ENGAGEMENT_STYLE },
];

// Map metric_key to category for dynamic report types
const METRIC_KEY_CATEGORY_MAP: Record<string, string> = {
  // Hardcoded equivalents
  cycle_summary: 'Cycles',
  review_cycle_extended: 'Cycles',
  cycle_users: 'Users',
  goal_progress_dashboard: 'Goals',
  goal_completion_rate: 'Goals',
  individual_goal_summary: 'Goals',
  goal_setting_compliance: 'Goals',
  survey_participation: 'Engagement',
  checkin_frequency: 'Engagement',
  // Known dynamic report types (from API)
  overall_employee_summary: 'Users',
  individual_employee_summary: 'Users',
  manager_level_summary: 'Users',
  department_score_summary: 'Users',
  my_team_dashboard: 'Users',
  my_performance_dashboard: 'Users',
  okr_progress_summary: 'Goals',
  performance_rating_distribution: 'Cycles',
  pip_report: 'Cycles',
  competency_assessment_report: 'Cycles',
};

// Infer category from metric_key and report name when not in the explicit map
// Order: check the most specific/people-related keywords first, then goals, engagement, then cycles as fallback
const inferCategory = (metricKey: string, name: string = ''): string => {
  const key = `${metricKey} ${name}`.toLowerCase();

  // Goals: goal, okr, objective (check early — specific)
  if (key.includes('goal') || key.includes('okr') || key.includes('objective')) return 'Goals';
  // Engagement: survey, checkin, feedback (check early — specific)
  if (key.includes('survey') || key.includes('checkin') || key.includes('check-in') ||
      key.includes('engagement') || key.includes('feedback') || key.includes('participation')) return 'Engagement';
  // Users: people-centric reports — employee, manager, team, individual, department
  if (key.includes('employee') || key.includes('manager') || key.includes('team') ||
      key.includes('individual') || key.includes('department') || key.includes('user') ||
      key.includes('my performance') || key.includes('my_performance')) return 'Users';
  // Everything else → Cycles (review cycles, performance, ratings, competency, PIP, etc.)
  return 'Cycles';
};

// Category styling for dynamic cards (PS only — Learn styles merged below after definition)
const CATEGORY_STYLE: Record<string, { iconBg: string; iconColor: string; gradient: string; badgeBg: string; badgeText: string }> = {
  Cycles: CYCLES_STYLE,
  Users: USERS_STYLE,
  Goals: GOALS_STYLE,
  Engagement: ENGAGEMENT_STYLE,
};

// Pick a distinct icon for dynamic report types based on metric_key keywords
const inferIcon = (metricKey: string, name: string): typeof ChartBar => {
  const key = `${metricKey} ${name}`.toLowerCase();
  if (key.includes('individual') || key.includes('employee')) return User;
  if (key.includes('manager')) return UsersThree;
  if (key.includes('department') || key.includes('org')) return Buildings;
  if (key.includes('score') || key.includes('rating')) return Star;
  if (key.includes('distribution')) return ChartPie;
  if (key.includes('progress') || key.includes('okr') || key.includes('trend')) return TrendUp;
  if (key.includes('compliance')) return ShieldCheck;
  if (key.includes('summary') || key.includes('overview')) return Table;
  if (key.includes('survey') || key.includes('participation')) return Megaphone;
  if (key.includes('checkin') || key.includes('frequency')) return CalendarCheck;
  if (key.includes('completion') || key.includes('rate')) return ChartPie;
  if (key.includes('goal')) return Target;
  if (key.includes('cycle') || key.includes('review')) return ChartBar;
  return ListMagnifyingGlass;
};

const CATEGORY_ORDER = ['Cycles', 'Users', 'Goals', 'Engagement'];

// Hardcoded metric keys that already have on-demand cards
const HARDCODED_METRIC_KEYS = new Set([
  'cycle_summary', 'review_cycle_extended', 'cycle_users',
  'goal_progress_dashboard', 'goal_completion_rate', 'individual_goal_summary', 'goal_setting_compliance',
  'survey_participation', 'checkin_frequency',
]);

// =============================================
// LEARN MODULE — Report Cards & Categories
// =============================================
type ModuleType = 'performspark' | 'learn';

const LEARN_COURSES_STYLE = { iconBg: 'bg-blue-50', iconColor: 'text-blue-600', gradient: 'from-blue-500 to-blue-600', badgeBg: 'bg-blue-50', badgeText: 'text-blue-700' };
const LEARN_ENROLLMENTS_STYLE = { iconBg: 'bg-violet-50', iconColor: 'text-violet-600', gradient: 'from-violet-500 to-violet-600', badgeBg: 'bg-violet-50', badgeText: 'text-violet-700' };
const LEARN_ILT_STYLE = { iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', gradient: 'from-emerald-500 to-emerald-600', badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700' };
const LEARN_PATHS_STYLE = { iconBg: 'bg-amber-50', iconColor: 'text-amber-600', gradient: 'from-amber-500 to-amber-600', badgeBg: 'bg-amber-50', badgeText: 'text-amber-700' };
const LEARN_COMPLIANCE_STYLE = { iconBg: 'bg-red-50', iconColor: 'text-red-600', gradient: 'from-red-500 to-red-600', badgeBg: 'bg-red-50', badgeText: 'text-red-700' };
const LEARN_COACHING_STYLE = { iconBg: 'bg-teal-50', iconColor: 'text-teal-600', gradient: 'from-teal-500 to-teal-600', badgeBg: 'bg-teal-50', badgeText: 'text-teal-700' };
const LEARN_SCORM_STYLE = { iconBg: 'bg-orange-50', iconColor: 'text-orange-600', gradient: 'from-orange-500 to-orange-600', badgeBg: 'bg-orange-50', badgeText: 'text-orange-700' };
const LEARN_USERS_STYLE = { iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600', gradient: 'from-indigo-500 to-indigo-600', badgeBg: 'bg-indigo-50', badgeText: 'text-indigo-700' };
const LEARN_CUSTOMERS_STYLE = { iconBg: 'bg-cyan-50', iconColor: 'text-cyan-600', gradient: 'from-cyan-500 to-cyan-600', badgeBg: 'bg-cyan-50', badgeText: 'text-cyan-700' };

const LEARN_CATEGORY_STYLE: Record<string, { iconBg: string; iconColor: string; gradient: string; badgeBg: string; badgeText: string }> = {
  Courses: LEARN_COURSES_STYLE,
  Enrollments: LEARN_ENROLLMENTS_STYLE,
  'ILT & Sessions': LEARN_ILT_STYLE,
  'Learning Paths': LEARN_PATHS_STYLE,
  Compliance: LEARN_COMPLIANCE_STYLE,
  Coaching: LEARN_COACHING_STYLE,
  'SCORM & Content': LEARN_SCORM_STYLE,
  'Users & Departments': LEARN_USERS_STYLE,
  Customers: LEARN_CUSTOMERS_STYLE,
};

const LEARN_CATEGORY_ORDER = ['Courses', 'Enrollments', 'ILT & Sessions', 'Learning Paths', 'Compliance', 'Coaching', 'SCORM & Content', 'Users & Departments', 'Customers'];

// Merge Learn category styles into the shared CATEGORY_STYLE map
Object.assign(CATEGORY_STYLE, LEARN_CATEGORY_STYLE);

// Category icons for the sidebar
const CATEGORY_ICONS: Record<string, typeof ChartBar> = {
  Cycles: ChartBar,
  Users: UsersThree,
  Goals: Target,
  Engagement: Megaphone,
  Courses: BookOpen,
  Enrollments: GraduationCap,
  'ILT & Sessions': CalendarCheck,
  'Learning Paths': Path,
  Compliance: ShieldCheck,
  Coaching: Chats,
  'SCORM & Content': PuzzlePiece,
  'Users & Departments': UsersThree,
  Customers: Buildings,
};

// Category-specific hover border classes for grid cards
const CATEGORY_HOVER_BORDER: Record<string, string> = {
  Cycles: 'hover:border-blue-200',
  Users: 'hover:border-violet-200',
  Goals: 'hover:border-emerald-200',
  Engagement: 'hover:border-amber-200',
  Courses: 'hover:border-blue-200',
  Enrollments: 'hover:border-violet-200',
  'ILT & Sessions': 'hover:border-emerald-200',
  'Learning Paths': 'hover:border-amber-200',
  Compliance: 'hover:border-red-200',
  Coaching: 'hover:border-teal-200',
  'SCORM & Content': 'hover:border-orange-200',
  'Users & Departments': 'hover:border-indigo-200',
  Customers: 'hover:border-cyan-200',
};

const LEARN_REPORT_CARDS: ReportCard[] = [
  // Courses (6)
  { key: 'learn_course_catalog', title: 'Course Catalog Summary', description: 'Total courses by type, mode, and status overview', category: 'Courses', icon: BookOpen, ...LEARN_COURSES_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_course_category', title: 'Course Category Distribution', description: 'Courses grouped by category to identify content gaps', category: 'Courses', icon: ChartBar, ...LEARN_COURSES_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_course_components', title: 'Course Component Breakdown', description: 'Components per course by type (SCORM, Quiz, Document, Video)', category: 'Courses', icon: PuzzlePiece, ...LEARN_COURSES_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_new_courses', title: 'New Courses Added', description: 'Courses created within a date range with creator details', category: 'Courses', icon: TrendUp, ...LEARN_COURSES_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_trainer_courses', title: 'Courses by Trainer', description: 'Which trainers are assigned to which courses', category: 'Courses', icon: User, ...LEARN_COURSES_STYLE, requiredPermission: 'learn.tms.trainers' },
  { key: 'learn_course_workrole', title: 'Courses by Work Role', description: 'Courses mapped to work roles for skill-gap analysis', category: 'Courses', icon: UsersThree, ...LEARN_COURSES_STYLE, requiredPermission: 'learn.lms.assignments' },
  // Enrollments (6)
  { key: 'learn_enrollment_summary', title: 'Enrollment Summary', description: 'Total enrollments by status with trend over time', category: 'Enrollments', icon: ChartBar, ...LEARN_ENROLLMENTS_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_enrollment_assignment', title: 'Enrollment by Assignment Type', description: 'Breakdown by how learners got enrolled: course, bundle, learning path', category: 'Enrollments', icon: List, ...LEARN_ENROLLMENTS_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_mandatory_optional', title: 'Mandatory vs Optional', description: 'Enrollments split by mandatory vs voluntary assignment', category: 'Enrollments', icon: ShieldCheck, ...LEARN_ENROLLMENTS_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_overdue_enrollments', title: 'Overdue Enrollments', description: 'Enrollments past due date that are still not completed', category: 'Enrollments', icon: Clock, ...LEARN_ENROLLMENTS_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_top_courses', title: 'Top Enrolled Courses', description: 'Most popular courses ranked by enrollment count', category: 'Enrollments', icon: Star, ...LEARN_ENROLLMENTS_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_learner_enrollment', title: 'Learner Enrollment Detail', description: 'Per-learner view of all courses, status, progress, and time spent', category: 'Enrollments', icon: User, ...LEARN_ENROLLMENTS_STYLE, requiredPermission: 'learn.lms.learning_records' },
  // ILT & Sessions (6)
  { key: 'learn_event_summary', title: 'Event Summary', description: 'All events by training type, mode, and state', category: 'ILT & Sessions', icon: CalendarCheck, ...LEARN_ILT_STYLE, requiredPermission: 'learn.tms.events' },
  { key: 'learn_session_schedule', title: 'Session Schedule', description: 'ILT sessions with dates, host, platform, and capacity', category: 'ILT & Sessions', icon: Clock, ...LEARN_ILT_STYLE, requiredPermission: 'learn.tms.events' },
  { key: 'learn_session_enrollment', title: 'Session Enrollment Status', description: 'Participants per session: invited, registered, waitlisted, attended', category: 'ILT & Sessions', icon: UsersThree, ...LEARN_ILT_STYLE, requiredPermission: 'learn.tms.events' },
  { key: 'learn_attendance_report', title: 'Attendance Report', description: 'Day-by-day attendance with check-in/out times and duration', category: 'ILT & Sessions', icon: ClipboardText, ...LEARN_ILT_STYLE, requiredPermission: 'learn.tms.events' },
  { key: 'learn_no_show_report', title: 'No-Show Report', description: 'Registered learners who were marked absent from sessions', category: 'ILT & Sessions', icon: User, ...LEARN_ILT_STYLE, requiredPermission: 'learn.tms.events' },
  { key: 'learn_trainer_utilization', title: 'Trainer Utilization', description: 'Sessions per trainer with total training hours delivered', category: 'ILT & Sessions', icon: TrendUp, ...LEARN_ILT_STYLE, requiredPermission: 'learn.tms.trainers' },
  // Learning Paths (3)
  { key: 'learn_lp_overview', title: 'Learning Path Overview', description: 'All learning paths with course count, sections, and requirements', category: 'Learning Paths', icon: Path, ...LEARN_PATHS_STYLE, requiredPermission: 'learn.lms.learning_journey' },
  { key: 'learn_lp_progress', title: 'Learning Path Progress', description: 'Learner step-by-step progress through learning paths', category: 'Learning Paths', icon: TrendUp, ...LEARN_PATHS_STYLE, requiredPermission: 'learn.lms.learning_journey' },
  { key: 'learn_lp_completion', title: 'Learning Path Completion', description: 'Completion rates and time-to-complete for learning paths', category: 'Learning Paths', icon: Certificate, ...LEARN_PATHS_STYLE, requiredPermission: 'learn.lms.learning_journey' },
  // Compliance (3)
  { key: 'learn_training_compliance', title: 'Training Compliance', description: 'Mandatory courses: who completed, who is overdue, compliance %', category: 'Compliance', icon: ShieldCheck, ...LEARN_COMPLIANCE_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_completion_rate', title: 'Course Completion Rate', description: 'Percentage of enrollments completed per course', category: 'Compliance', icon: ChartPie, ...LEARN_COMPLIANCE_STYLE, requiredPermission: 'learn.lms.assignments' },
  { key: 'learn_time_to_complete', title: 'Time-to-Completion', description: 'Average time from enrollment to completion per course', category: 'Compliance', icon: Clock, ...LEARN_COMPLIANCE_STYLE, requiredPermission: 'learn.lms.assignments' },
  // Coaching (4)
  { key: 'learn_coaching_summary', title: 'Coaching Engagement Summary', description: 'Engagements by status with completed vs total sessions', category: 'Coaching', icon: Chats, ...LEARN_COACHING_STYLE, requiredPermission: 'learn.coaching.engagements' },
  { key: 'learn_coach_workload', title: 'Coach Workload', description: 'Number of active engagements per coach', category: 'Coaching', icon: User, ...LEARN_COACHING_STYLE, requiredPermission: 'learn.coaching.coaches' },
  { key: 'learn_coaching_sessions', title: 'Coaching Session Log', description: 'All coaching sessions with dates, notes, and duration', category: 'Coaching', icon: CalendarCheck, ...LEARN_COACHING_STYLE, requiredPermission: 'learn.coaching.sessions' },
  { key: 'learn_coaching_assessment', title: 'Coaching Assessment', description: 'Assessment attributes and ratings given during coaching', category: 'Coaching', icon: Star, ...LEARN_COACHING_STYLE, requiredPermission: 'learn.coaching.engagements' },
  // SCORM & Content (3)
  { key: 'learn_scorm_activity', title: 'SCORM Completion & Score', description: 'Lesson status, score, and session time per SCORM module', category: 'SCORM & Content', icon: PuzzlePiece, ...LEARN_SCORM_STYLE, requiredPermission: 'learn.lms.learning_records' },
  { key: 'learn_scorm_attempts', title: 'SCORM Attempt Tracking', description: 'Number of attempts and resume tracking per user per module', category: 'SCORM & Content', icon: List, ...LEARN_SCORM_STYLE, requiredPermission: 'learn.lms.learning_records' },
  { key: 'learn_content_usage', title: 'Content Usage Report', description: 'Usage stats across all content types: video, document, quiz, URL', category: 'SCORM & Content', icon: VideoCamera, ...LEARN_SCORM_STYLE, requiredPermission: 'learn.lms.learning_records' },
  // Users & Departments (2)
  { key: 'learn_user_summary', title: 'User Learning Summary', description: 'Per-user overview with course counts by enrollment status', category: 'Users & Departments', icon: User, ...LEARN_USERS_STYLE, requiredPermission: 'learn.lms.learning_records' },
  { key: 'learn_department_summary', title: 'Department Learning Summary', description: 'Learning statistics rolled up by organizational unit', category: 'Users & Departments', icon: Buildings, ...LEARN_USERS_STYLE, requiredPermission: 'learn.lms.learning_records' },
  // Bundles (1)
  { key: 'learn_bundle_summary', title: 'Bundle Summary', description: 'Course bundles with enrollment counts and completion rates', category: 'Courses', icon: List, ...LEARN_COURSES_STYLE, requiredPermission: 'learn.lms.assignments' },
  // Customers (2)
  { key: 'learn_policyholder_overview', title: 'Customer Overview', description: 'Customer organizations with learner counts and completion rates', category: 'Customers', icon: Buildings, ...LEARN_CUSTOMERS_STYLE, requiredPermission: 'learn.lms.customer_assignments' },
  { key: 'learn_policyholder_enrollments', title: 'Customer Enrollments', description: 'Per-learner enrollment detail for customer employees', category: 'Customers', icon: User, ...LEARN_CUSTOMERS_STYLE, requiredPermission: 'learn.lms.customer_assignments' },
];

// Column alias mappings for Goal sub-views
const GOAL_PROGRESS_COLUMN_ALIASES: Record<string, string> = {
  cycle_name: 'Cycle',
  cycle_status: 'Status',
  framework: 'Framework',
  total_goals: 'Total Goals',
  on_track_count: 'On Track',
  at_risk_count: 'At Risk',
  behind_count: 'Behind',
  completed_count: 'Completed',
  avg_progress: 'Avg Progress',
  overdue_count: 'Overdue',
};

const GOAL_COMPLETION_COLUMN_ALIASES: Record<string, string> = {
  employee_name: 'Employee',
  email: 'Email',
  org_unit: 'Org Unit',
  total_goals: 'Total Goals',
  completed_goals: 'Completed',
  completion_rate: 'Completion %',
  avg_progress: 'Avg Progress',
  overdue_goals: 'Overdue',
};

const GOAL_DETAILS_COLUMN_ALIASES: Record<string, string> = {
  employee_name: 'Employee',
  goal_title: 'Goal',
  cycle_name: 'Cycle',
  category: 'Category',
  status: 'Status',
  priority: 'Priority',
  progress: 'Progress',
  goal_level: 'Level',
  start_date: 'Start Date',
  end_date: 'End Date',
  updated_at: 'Updated',
};

const GOAL_PROGRESS_DISPLAY_COLUMNS = [
  'cycle_name', 'cycle_status', 'framework', 'total_goals',
  'on_track_count', 'at_risk_count', 'behind_count',
  'completed_count', 'avg_progress', 'overdue_count',
];

const GOAL_COMPLETION_DISPLAY_COLUMNS = [
  'employee_name', 'email', 'org_unit', 'total_goals',
  'completed_goals', 'completion_rate', 'avg_progress', 'overdue_goals',
];

const GOAL_DETAILS_DISPLAY_COLUMNS = [
  'employee_name', 'goal_title', 'cycle_name', 'category',
  'status', 'priority', 'progress', 'goal_level',
  'start_date', 'end_date', 'updated_at',
];

// Column alias mappings for Goal Setting Compliance
const GOAL_COMPLIANCE_COLUMN_ALIASES: Record<string, string> = {
  employee_name: 'Employee',
  email: 'Email',
  org_unit: 'Org Unit',
  total_goals: 'Total Goals',
  completed_goals: 'Completed',
  overdue_goals: 'Overdue',
  has_goals: 'Has Goals',
  earliest_goal_date: 'Earliest Goal',
  latest_deadline: 'Latest Deadline',
};

const GOAL_COMPLIANCE_DISPLAY_COLUMNS = [
  'employee_name', 'email', 'org_unit', 'total_goals',
  'completed_goals', 'overdue_goals', 'has_goals',
  'earliest_goal_date', 'latest_deadline',
];

// Column alias mappings for Review Cycle Extended
const REVIEW_EXTENDED_COLUMN_ALIASES: Record<string, string> = {
  cycle_name: 'Cycle Name',
  cycle_type: 'Type',
  total_participants: 'Participants',
  assigned_count: 'Assigned',
  in_progress_count: 'In Progress',
  completed_count: 'Completed',
  completion_rate: 'Completion %',
  overdue_count: 'Overdue',
  created_on: 'Created',
};

const REVIEW_EXTENDED_DISPLAY_COLUMNS = [
  'cycle_name', 'cycle_type', 'total_participants',
  'assigned_count', 'in_progress_count', 'completed_count',
  'completion_rate', 'overdue_count', 'created_on',
];

// Column alias mappings for Survey Participation
const SURVEY_COLUMN_ALIASES: Record<string, string> = {
  survey_title: 'Survey',
  survey_type: 'Type',
  start_date: 'Start Date',
  end_date: 'End Date',
  total_invited: 'Invited',
  total_responded: 'Responded',
  response_rate: 'Response %',
  is_anonymous: 'Anonymous',
  is_closed: 'Closed',
};

const SURVEY_DISPLAY_COLUMNS = [
  'survey_title', 'survey_type', 'start_date', 'end_date',
  'total_invited', 'total_responded', 'response_rate',
  'is_anonymous', 'is_closed',
];

// Column alias mappings for Check-in Frequency
const CHECKIN_COLUMN_ALIASES: Record<string, string> = {
  employee_name: 'Employee',
  email: 'Email',
  org_unit: 'Org Unit',
  manager_name: 'Manager',
  total_checkins: 'Total Check-ins',
  total_cycles: 'Total Cycles',
  avg_checkins_per_cycle: 'Avg / Cycle',
};

const CHECKIN_DISPLAY_COLUMNS = [
  'employee_name', 'email', 'org_unit', 'manager_name',
  'total_checkins', 'total_cycles', 'avg_checkins_per_cycle',
];

// Review Cloud API base URL (same as useReviewCloudData hook)
const REVIEW_CLOUD_API_BASE_URL = import.meta.env.VITE_REVIEW_CLOUD_API_BASE_URL || 'http://localhost:8001/api/reviews';

// Review drill-down display columns
const REVIEW_DISPLAY_COLUMNS = [
  'employeeName', 'email', 'jobTitle', 'department', 'manager',
  'status', 'empStatus', 'mgrStatus', 'dueDate', 'modifiedDate',
];

const REVIEW_COLUMN_ALIASES: Record<string, string> = {
  employeeName: 'Employee',
  email: 'Email',
  jobTitle: 'Job Title',
  department: 'Department',
  manager: 'Manager',
  status: 'Status',
  empStatus: 'Employee Status',
  mgrStatus: 'Manager Status',
  dueDate: 'Due Date',
  modifiedDate: 'Last Modified',
};

const REVIEW_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Assigned', label: 'Assigned' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
];

// Drill-down configuration: maps parent report → detail report + filter derivation
// 'reviewCloud' type means call Review Cloud's listAllReviews API
// 'analytics' type means call the analytics API with a more detailed report_key and a filter
interface DrillDownConfig {
  type: 'reviewCloud' | 'analytics';
  // For analytics type: which report_key to fetch for detail
  detailReportKey?: string;
  // How to derive the filter from the clicked row
  getFilter: (row: any) => { field: string; operator: string; value: any };
  // Build title from the row data
  getTitle: (row: any) => string;
  // Search fields for the detail view
  searchFields: string[];
  // Column config for the detail view
  columnAliases: Record<string, string>;
  displayColumns: string[];
}

const DRILL_DOWN_MAP: Partial<Record<string, DrillDownConfig>> = {
  // Cycles → individual reviews (Review Cloud API)
  cycle_summary: {
    type: 'reviewCloud',
    getFilter: (row) => ({ field: 'cycleId', operator: '=', value: row.id ?? row.cycle_id }),
    getTitle: (row) => row.cy_title || row.cycle_name || 'Cycle',
    searchFields: ['employeeName'],
    columnAliases: REVIEW_COLUMN_ALIASES,
    displayColumns: REVIEW_DISPLAY_COLUMNS,
  },
  review_cycle_extended: {
    type: 'reviewCloud',
    getFilter: (row) => ({ field: 'cycleId', operator: '=', value: row.cycle_id ?? row.id }),
    getTitle: (row) => row.cycle_name || row.cy_title || 'Cycle',
    searchFields: ['employeeName'],
    columnAliases: REVIEW_COLUMN_ALIASES,
    displayColumns: REVIEW_DISPLAY_COLUMNS,
  },
  // Users → individual reviews for that user (Review Cloud API, search by name)
  cycle_users: {
    type: 'reviewCloud',
    getFilter: (row) => ({ field: 'searchParam', operator: '=', value: `${row.first_name || ''} ${row.last_name || ''}`.trim() }),
    getTitle: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email || 'User',
    searchFields: ['employeeName'],
    columnAliases: REVIEW_COLUMN_ALIASES,
    displayColumns: REVIEW_DISPLAY_COLUMNS,
  },
  // Goal Progress → individual goals filtered by cycle
  progress_dashboard: {
    type: 'analytics',
    detailReportKey: 'individual_goal_summary',
    getFilter: (row) => ({ field: 'cycle_name', operator: '=', value: row.cycle_name }),
    getTitle: (row) => row.cycle_name || 'Goals',
    searchFields: ['employee_name', 'goal_title'],
    columnAliases: GOAL_DETAILS_COLUMN_ALIASES,
    displayColumns: GOAL_DETAILS_DISPLAY_COLUMNS,
  },
  // Completion Rate → individual goals for that employee
  completion_rate: {
    type: 'analytics',
    detailReportKey: 'individual_goal_summary',
    getFilter: (row) => ({ field: 'employee_name', operator: '=', value: row.employee_name }),
    getTitle: (row) => row.employee_name || 'Employee Goals',
    searchFields: ['goal_title'],
    columnAliases: GOAL_DETAILS_COLUMN_ALIASES,
    displayColumns: GOAL_DETAILS_DISPLAY_COLUMNS,
  },
  // Goal Compliance → individual goals for that employee
  goal_setting_compliance: {
    type: 'analytics',
    detailReportKey: 'individual_goal_summary',
    getFilter: (row) => ({ field: 'employee_name', operator: '=', value: row.employee_name }),
    getTitle: (row) => row.employee_name || 'Employee Goals',
    searchFields: ['goal_title'],
    columnAliases: GOAL_DETAILS_COLUMN_ALIASES,
    displayColumns: GOAL_DETAILS_DISPLAY_COLUMNS,
  },
  // Check-in Frequency → individual reviews for that user (search by name)
  checkin_frequency: {
    type: 'reviewCloud',
    getFilter: (row) => ({ field: 'searchParam', operator: '=', value: row.employee_name || '' }),
    getTitle: (row) => row.employee_name || 'Employee',
    searchFields: ['employeeName'],
    columnAliases: REVIEW_COLUMN_ALIASES,
    displayColumns: REVIEW_DISPLAY_COLUMNS,
  },

  // ---- Learn Drill-Downs ----

  // Category Distribution → Course Catalog (which courses in this category)
  learn_course_category: {
    type: 'analytics',
    detailReportKey: 'learn_course_catalog',
    getFilter: (row) => ({ field: 'category_name', operator: '=', value: row.category_name }),
    getTitle: (row) => row.category_name || 'Category Courses',
    searchFields: ['title'],
    columnAliases: { course_id: 'ID', title: 'Title', type: 'Type', mode: 'Mode', course_type: 'Course Type', status: 'Status', duration: 'Duration', component_count: 'Components', enrollment_count: 'Enrollments' },
    displayColumns: ['title', 'type', 'mode', 'course_type', 'status', 'duration', 'component_count', 'enrollment_count'],
  },
  // Course Catalog → Course Components
  learn_course_catalog: {
    type: 'analytics',
    detailReportKey: 'learn_course_components',
    getFilter: (row) => ({ field: 'course_id', operator: '=', value: row.course_id }),
    getTitle: (row) => row.title || 'Course Components',
    searchFields: ['component_name'],
    columnAliases: { course_id: 'Course', course_title: 'Course', component_name: 'Component', component_type: 'Type', duration: 'Duration', is_required: 'Required', passing_score: 'Pass Score', course_order: 'Order' },
    displayColumns: ['component_name', 'component_type', 'duration', 'is_required', 'passing_score', 'course_order'],
  },
  // New Courses → Course Components
  learn_new_courses: {
    type: 'analytics',
    detailReportKey: 'learn_course_components',
    getFilter: (row) => ({ field: 'course_id', operator: '=', value: row.course_id }),
    getTitle: (row) => row.title || 'Course Components',
    searchFields: ['component_name'],
    columnAliases: { component_name: 'Component', component_type: 'Type', duration: 'Duration', is_required: 'Required', passing_score: 'Pass Score', course_order: 'Order' },
    displayColumns: ['component_name', 'component_type', 'duration', 'is_required', 'passing_score', 'course_order'],
  },
  // Enrollment Summary → Learner Enrollment Detail
  learn_enrollment_summary: {
    type: 'analytics',
    detailReportKey: 'learn_learner_enrollment',
    getFilter: (row) => ({ field: 'course_id', operator: '=', value: row.course_id }),
    getTitle: (row) => row.course_title || 'Enrollments',
    searchFields: ['employee_name'],
    columnAliases: { employee_name: 'Employee', email: 'Email', course_title: 'Course', progress_status: 'Status', progress_percent: 'Progress', time_spent: 'Time Spent', assignment_category: 'Assignment', enrolled_at: 'Enrolled', completed_at: 'Completed', due_date: 'Due Date' },
    displayColumns: ['employee_name', 'email', 'progress_status', 'progress_percent', 'time_spent', 'assignment_category', 'enrolled_at', 'completed_at', 'due_date'],
  },
  // Event Summary → Session Schedule
  learn_event_summary: {
    type: 'analytics',
    detailReportKey: 'learn_session_schedule',
    getFilter: (row) => ({ field: 'event_id', operator: '=', value: row.event_id }),
    getTitle: (row) => row.event_name || 'Sessions',
    searchFields: ['meeting_topic'],
    columnAliases: { session_id: 'Session', event_name: 'Event', meeting_topic: 'Topic', platform: 'Platform', start_date: 'Date', start_time: 'Start', end_time: 'End', duration_minutes: 'Duration (min)', max_participants: 'Max', meeting_status: 'Status', host_email: 'Host', day_count: 'Days' },
    displayColumns: ['meeting_topic', 'platform', 'start_date', 'start_time', 'end_time', 'duration_minutes', 'max_participants', 'meeting_status', 'host_email', 'day_count'],
  },
  // LP Overview → LP Progress
  learn_lp_overview: {
    type: 'analytics',
    detailReportKey: 'learn_lp_progress',
    getFilter: (row) => ({ field: 'lp_id', operator: '=', value: row.lp_id }),
    getTitle: (row) => row.lp_title || 'Learning Path Progress',
    searchFields: ['employee_name'],
    columnAliases: { employee_name: 'Employee', email: 'Email', lp_title: 'Learning Path', progress_status: 'Status', progress_percent: 'Progress', child_course_title: 'Course', child_status: 'Course Status', child_progress: 'Course Progress', enrolled_at: 'Enrolled' },
    displayColumns: ['employee_name', 'email', 'progress_status', 'progress_percent', 'child_course_title', 'child_status', 'child_progress', 'enrolled_at'],
  },
  // Coaching Summary → Coaching Sessions
  learn_coaching_summary: {
    type: 'analytics',
    detailReportKey: 'learn_coaching_sessions',
    getFilter: (row) => ({ field: 'engagement_code', operator: '=', value: row.engagement_code }),
    getTitle: (row) => row.program_name || row.engagement_code || 'Coaching Sessions',
    searchFields: ['coach_name', 'coachee_name'],
    columnAliases: { engagement_code: 'Code', program_name: 'Program', coach_name: 'Coach', coachee_name: 'Coachee', status: 'Status', scheduled_date_time: 'Scheduled', completed_sessions: 'Completed', total_sessions: 'Total', notes: 'Notes' },
    displayColumns: ['coach_name', 'coachee_name', 'status', 'scheduled_date_time', 'completed_sessions', 'total_sessions', 'notes'],
  },
  // User Summary → Learner Enrollment Detail (by email)
  learn_user_summary: {
    type: 'analytics',
    detailReportKey: 'learn_learner_enrollment',
    getFilter: (row) => ({ field: 'email', operator: '=', value: row.email }),
    getTitle: (row) => row.employee_name || 'Learner Courses',
    searchFields: ['course_title'],
    columnAliases: { course_title: 'Course', course_type: 'Type', progress_status: 'Status', progress_percent: 'Progress', time_spent: 'Time Spent', enrolled_at: 'Enrolled', completed_at: 'Completed', due_date: 'Due Date' },
    displayColumns: ['course_title', 'course_type', 'progress_status', 'progress_percent', 'time_spent', 'enrolled_at', 'completed_at', 'due_date'],
  },
  // Policyholder Overview → Policyholder Enrollments
  learn_policyholder_overview: {
    type: 'analytics',
    detailReportKey: 'learn_policyholder_enrollments',
    getFilter: (row) => ({ field: 'customer_name', operator: '=', value: row.customer_name }),
    getTitle: (row) => row.customer_name || 'Customer Enrollments',
    searchFields: ['employee_name', 'course_title'],
    columnAliases: { customer_name: 'Customer', employee_name: 'Employee', email: 'Email', course_title: 'Course', progress_status: 'Status', progress_percent: 'Progress', enrolled_at: 'Enrolled', completed_at: 'Completed' },
    displayColumns: ['employee_name', 'email', 'course_title', 'progress_status', 'progress_percent', 'enrolled_at', 'completed_at'],
  },
  // Learner Enrollment → Enrollment Summary (reverse: by employee email)
  learn_learner_enrollment: {
    type: 'analytics',
    detailReportKey: 'learn_learner_enrollment',
    getFilter: (row) => ({ field: 'email', operator: '=', value: row.email }),
    getTitle: (row) => row.employee_name || 'Learner Courses',
    searchFields: ['course_title'],
    columnAliases: { employee_name: 'Employee', email: 'Email', course_title: 'Course', course_type: 'Type', progress_status: 'Status', progress_percent: 'Progress', time_spent: 'Time Spent', enrolled_at: 'Enrolled', completed_at: 'Completed', due_date: 'Due Date' },
    displayColumns: ['course_title', 'course_type', 'progress_status', 'progress_percent', 'time_spent', 'enrolled_at', 'completed_at', 'due_date'],
  },
  // Top Courses → Learner Enrollment (who's enrolled in this top course)
  learn_top_courses: {
    type: 'analytics',
    detailReportKey: 'learn_learner_enrollment',
    getFilter: (row) => ({ field: 'course_title', operator: '=', value: row.course_title }),
    getTitle: (row) => row.course_title || 'Course Enrollments',
    searchFields: ['employee_name'],
    columnAliases: { employee_name: 'Employee', email: 'Email', progress_status: 'Status', progress_percent: 'Progress', time_spent: 'Time Spent', assignment_category: 'Assignment', enrolled_at: 'Enrolled', completed_at: 'Completed', due_date: 'Due Date' },
    displayColumns: ['employee_name', 'email', 'progress_status', 'progress_percent', 'time_spent', 'assignment_category', 'enrolled_at', 'completed_at', 'due_date'],
  },
  // Training Compliance → Learner Enrollment (who's overdue on mandatory course)
  learn_training_compliance: {
    type: 'analytics',
    detailReportKey: 'learn_learner_enrollment',
    getFilter: (row) => ({ field: 'course_title', operator: '=', value: row.course_title }),
    getTitle: (row) => row.course_title || 'Compliance Detail',
    searchFields: ['employee_name'],
    columnAliases: { employee_name: 'Employee', email: 'Email', progress_status: 'Status', progress_percent: 'Progress', time_spent: 'Time Spent', enrolled_at: 'Enrolled', completed_at: 'Completed', due_date: 'Due Date' },
    displayColumns: ['employee_name', 'email', 'progress_status', 'progress_percent', 'time_spent', 'enrolled_at', 'completed_at', 'due_date'],
  },
  // Completion Rate → Learner Enrollment (who completed, who didn't)
  learn_completion_rate: {
    type: 'analytics',
    detailReportKey: 'learn_learner_enrollment',
    getFilter: (row) => ({ field: 'course_title', operator: '=', value: row.course_title }),
    getTitle: (row) => row.course_title || 'Completion Detail',
    searchFields: ['employee_name'],
    columnAliases: { employee_name: 'Employee', email: 'Email', progress_status: 'Status', progress_percent: 'Progress', time_spent: 'Time Spent', enrolled_at: 'Enrolled', completed_at: 'Completed', due_date: 'Due Date' },
    displayColumns: ['employee_name', 'email', 'progress_status', 'progress_percent', 'time_spent', 'enrolled_at', 'completed_at', 'due_date'],
  },
  // Coach Workload → Coaching Summary (which engagements does this coach have)
  learn_coach_workload: {
    type: 'analytics',
    detailReportKey: 'learn_coaching_summary',
    getFilter: (row) => ({ field: 'coach_email', operator: '=', value: row.coach_email }),
    getTitle: (row) => row.coach_name || 'Coach Engagements',
    searchFields: ['program_name', 'coachee_name'],
    columnAliases: { engagement_code: 'Code', program_name: 'Program', coach_name: 'Coach', coachee_name: 'Coachee', status: 'Status', progress: 'Progress', completed_sessions: 'Completed', total_sessions: 'Total', start_date: 'Start', end_date: 'End' },
    displayColumns: ['engagement_code', 'program_name', 'coachee_name', 'status', 'completed_sessions', 'total_sessions', 'start_date', 'end_date'],
  },
  // Session Schedule → Session Enrollment (who's registered for this session)
  learn_session_schedule: {
    type: 'analytics',
    detailReportKey: 'learn_session_enrollment',
    getFilter: (row) => ({ field: 'event_name', operator: '=', value: row.event_name }),
    getTitle: (row) => row.meeting_topic || row.event_name || 'Session Participants',
    searchFields: ['employee_name'],
    columnAliases: { employee_name: 'Employee', event_name: 'Event', meeting_topic: 'Session', enrollment_status: 'Status', invited_on: 'Invited', registered_on: 'Registered', attended_on: 'Attended', waitlist_position: 'Waitlist' },
    displayColumns: ['employee_name', 'event_name', 'meeting_topic', 'enrollment_status', 'invited_on', 'registered_on', 'attended_on'],
  },
};

// Format date to "17 Nov 2025" format
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

// Get date range display string
const getDateRangeDisplay = (timeRange: string, customStart?: string, customEnd?: string): string => {
  const now = new Date();
  let startDate: Date;

  const formatShort = (d: Date) => d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  if (timeRange === 'custom') {
    const start = customStart ? formatShort(new Date(customStart)) : 'Start';
    const end = customEnd ? formatShort(new Date(customEnd)) : 'End';
    return start + ' - ' + end;
  }

  switch (timeRange) {
    case '7d':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '90d':
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case '1y':
      startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      break;
    default:
      return 'All Time';
  }

  return `${formatShort(startDate)} - ${formatShort(now)}`;
};

// Convert API columns to DataGrid columns for Cycles
const convertCycleColumns = (apiColumns: ColumnInfo[]): ColumnDef<any>[] => {
  const orderedColumns: ColumnDef<any>[] = [];

  for (const fieldName of CYCLE_DISPLAY_COLUMNS) {
    const apiCol = apiColumns.find(col => col.name === fieldName);
    if (apiCol) {
      const colDef: ColumnDef<any> = {
        accessorKey: apiCol.name,
        header: makeSortableHeader(CYCLE_COLUMN_ALIASES[apiCol.name] || apiCol.label),
        enableSorting: apiCol.sortable,
        size: fieldName === 'cy_title' ? 300 : 150,
      };

      if (fieldName === 'cy_created_on' || fieldName === 'cy_stdate' || fieldName === 'cy_endate') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-600">{formatDate(getValue() as string)}</span>
        );
      } else if (fieldName === 'cy_title') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-900 font-medium">{getValue() as string}</span>
        );
      } else if (['total_reviews', 'assigned_count', 'in_progress_count', 'completed_count'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}</span>
          </div>
        );
      }

      orderedColumns.push(colDef);
    }
  }

  return orderedColumns;
};

// Convert API columns to DataGrid columns for Users
const convertUserColumns = (apiColumns: ColumnInfo[]): ColumnDef<any>[] => {
  const orderedColumns: ColumnDef<any>[] = [];

  for (const fieldName of USER_DISPLAY_COLUMNS) {
    const apiCol = apiColumns.find(col => col.name === fieldName);
    if (apiCol) {
      const colDef: ColumnDef<any> = {
        accessorKey: apiCol.name,
        header: makeSortableHeader(USER_COLUMN_ALIASES[apiCol.name] || apiCol.label),
        enableSorting: apiCol.sortable,
        size: fieldName === 'email' ? 250 : 150,
      };

      // Style name columns as links (primary color)
      if (fieldName === 'first_name' || fieldName === 'last_name') {
        colDef.cell = ({ getValue }) => (
          <span className="text-primary cursor-pointer hover:underline">{getValue() as string}</span>
        );
      } else if (['review_count', 'assigned_count', 'in_progress_count', 'completed_count'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}</span>
          </div>
        );
      }

      orderedColumns.push(colDef);
    }
  }

  return orderedColumns;
};

// Convert API columns to DataGrid columns for Goal Progress Dashboard
const convertGoalProgressColumns = (apiColumns: ColumnInfo[]): ColumnDef<any>[] => {
  const orderedColumns: ColumnDef<any>[] = [];
  for (const fieldName of GOAL_PROGRESS_DISPLAY_COLUMNS) {
    const apiCol = apiColumns.find(col => col.name === fieldName);
    if (apiCol) {
      const colDef: ColumnDef<any> = {
        accessorKey: apiCol.name,
        header: makeSortableHeader(GOAL_PROGRESS_COLUMN_ALIASES[apiCol.name] || apiCol.label),
        enableSorting: apiCol.sortable,
        size: fieldName === 'cycle_name' ? 250 : 120,
      };
      if (fieldName === 'cycle_name') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-900 font-medium">{getValue() as string}</span>
        );
      } else if (fieldName === 'avg_progress') {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}%</span>
          </div>
        );
      } else if (['total_goals', 'on_track_count', 'at_risk_count', 'behind_count', 'completed_count', 'overdue_count'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}</span>
          </div>
        );
      }
      orderedColumns.push(colDef);
    }
  }
  return orderedColumns;
};

// Convert API columns to DataGrid columns for Goal Completion Rate
const convertGoalCompletionColumns = (apiColumns: ColumnInfo[]): ColumnDef<any>[] => {
  const orderedColumns: ColumnDef<any>[] = [];
  for (const fieldName of GOAL_COMPLETION_DISPLAY_COLUMNS) {
    const apiCol = apiColumns.find(col => col.name === fieldName);
    if (apiCol) {
      const colDef: ColumnDef<any> = {
        accessorKey: apiCol.name,
        header: makeSortableHeader(GOAL_COMPLETION_COLUMN_ALIASES[apiCol.name] || apiCol.label),
        enableSorting: apiCol.sortable,
        size: fieldName === 'employee_name' ? 200 : fieldName === 'email' ? 250 : 120,
      };
      if (fieldName === 'employee_name') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-900 font-medium">{getValue() as string}</span>
        );
      } else if (fieldName === 'completion_rate' || fieldName === 'avg_progress') {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}%</span>
          </div>
        );
      } else if (['total_goals', 'completed_goals', 'overdue_goals'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}</span>
          </div>
        );
      }
      orderedColumns.push(colDef);
    }
  }
  return orderedColumns;
};

// Convert API columns to DataGrid columns for Individual Goal Summary
const convertGoalDetailsColumns = (apiColumns: ColumnInfo[]): ColumnDef<any>[] => {
  const orderedColumns: ColumnDef<any>[] = [];
  for (const fieldName of GOAL_DETAILS_DISPLAY_COLUMNS) {
    const apiCol = apiColumns.find(col => col.name === fieldName);
    if (apiCol) {
      const colDef: ColumnDef<any> = {
        accessorKey: apiCol.name,
        header: makeSortableHeader(GOAL_DETAILS_COLUMN_ALIASES[apiCol.name] || apiCol.label),
        enableSorting: apiCol.sortable,
        size: fieldName === 'goal_title' ? 250 : fieldName === 'employee_name' ? 200 : 120,
      };
      if (fieldName === 'employee_name') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-900 font-medium">{getValue() as string}</span>
        );
      } else if (fieldName === 'goal_title') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-900">{getValue() as string}</span>
        );
      } else if (fieldName === 'progress') {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}%</span>
          </div>
        );
      } else if (['start_date', 'end_date', 'updated_at'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-600">{formatDate(getValue() as string)}</span>
        );
      } else if (fieldName === 'status') {
        colDef.cell = ({ getValue }) => {
          const status = getValue() as string;
          const colorClass = status === 'Completed' || status === 'Closed'
            ? 'text-green-700 bg-green-50'
            : status === 'InProgress' || status === 'In Progress'
              ? 'text-blue-700 bg-blue-50'
              : 'text-gray-700 bg-gray-50';
          return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
              {status || '-'}
            </span>
          );
        };
      } else if (fieldName === 'priority') {
        colDef.cell = ({ getValue }) => {
          const priority = getValue() as string;
          const colorClass = priority === 'High'
            ? 'text-red-700 bg-red-50'
            : priority === 'Medium'
              ? 'text-yellow-700 bg-yellow-50'
              : 'text-gray-700 bg-gray-50';
          return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
              {priority || '-'}
            </span>
          );
        };
      }
      orderedColumns.push(colDef);
    }
  }
  return orderedColumns;
};

// Convert API columns to DataGrid columns for Goal Setting Compliance
const convertGoalComplianceColumns = (apiColumns: ColumnInfo[]): ColumnDef<any>[] => {
  const orderedColumns: ColumnDef<any>[] = [];
  for (const fieldName of GOAL_COMPLIANCE_DISPLAY_COLUMNS) {
    const apiCol = apiColumns.find(col => col.name === fieldName);
    if (apiCol) {
      const colDef: ColumnDef<any> = {
        accessorKey: apiCol.name,
        header: makeSortableHeader(GOAL_COMPLIANCE_COLUMN_ALIASES[apiCol.name] || apiCol.label),
        enableSorting: apiCol.sortable,
        size: fieldName === 'employee_name' ? 200 : fieldName === 'email' ? 250 : 120,
      };
      if (fieldName === 'employee_name') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-900 font-medium">{getValue() as string}</span>
        );
      } else if (fieldName === 'has_goals') {
        colDef.cell = ({ getValue }) => {
          const val = getValue() as string;
          const isYes = val === 'Yes';
          return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${isYes ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
              {val}
            </span>
          );
        };
      } else if (['earliest_goal_date', 'latest_deadline'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-600">{formatDate(getValue() as string)}</span>
        );
      } else if (['total_goals', 'completed_goals', 'overdue_goals'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}</span>
          </div>
        );
      }
      orderedColumns.push(colDef);
    }
  }
  return orderedColumns;
};

// Convert API columns to DataGrid columns for Review Cycle Extended
const convertReviewExtendedColumns = (apiColumns: ColumnInfo[]): ColumnDef<any>[] => {
  const orderedColumns: ColumnDef<any>[] = [];
  for (const fieldName of REVIEW_EXTENDED_DISPLAY_COLUMNS) {
    const apiCol = apiColumns.find(col => col.name === fieldName);
    if (apiCol) {
      const colDef: ColumnDef<any> = {
        accessorKey: apiCol.name,
        header: makeSortableHeader(REVIEW_EXTENDED_COLUMN_ALIASES[apiCol.name] || apiCol.label),
        enableSorting: apiCol.sortable,
        size: fieldName === 'cycle_name' ? 300 : 150,
      };
      if (fieldName === 'cycle_name') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-900 font-medium">{getValue() as string}</span>
        );
      } else if (fieldName === 'completion_rate') {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}%</span>
          </div>
        );
      } else if (fieldName === 'created_on') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-600">{formatDate(getValue() as string)}</span>
        );
      } else if (['total_participants', 'assigned_count', 'in_progress_count', 'completed_count', 'overdue_count'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}</span>
          </div>
        );
      }
      orderedColumns.push(colDef);
    }
  }
  return orderedColumns;
};

// Convert API columns to DataGrid columns for Survey Participation
const convertSurveyColumns = (apiColumns: ColumnInfo[]): ColumnDef<any>[] => {
  const orderedColumns: ColumnDef<any>[] = [];
  for (const fieldName of SURVEY_DISPLAY_COLUMNS) {
    const apiCol = apiColumns.find(col => col.name === fieldName);
    if (apiCol) {
      const colDef: ColumnDef<any> = {
        accessorKey: apiCol.name,
        header: makeSortableHeader(SURVEY_COLUMN_ALIASES[apiCol.name] || apiCol.label),
        enableSorting: apiCol.sortable,
        size: fieldName === 'survey_title' ? 250 : 120,
      };
      if (fieldName === 'survey_title') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-900 font-medium">{getValue() as string}</span>
        );
      } else if (['start_date', 'end_date'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-600">{formatDate(getValue() as string)}</span>
        );
      } else if (fieldName === 'response_rate') {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}%</span>
          </div>
        );
      } else if (['is_anonymous', 'is_closed'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => {
          const val = getValue() as string;
          const isYes = val === 'Yes' || val === 'true';
          return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${isYes ? 'text-green-700 bg-green-50' : 'text-gray-700 bg-gray-50'}`}>
              {isYes ? 'Yes' : 'No'}
            </span>
          );
        };
      } else if (['total_invited', 'total_responded'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}</span>
          </div>
        );
      }
      orderedColumns.push(colDef);
    }
  }
  return orderedColumns;
};

// Convert API columns to DataGrid columns for Check-in Frequency
const convertCheckinColumns = (apiColumns: ColumnInfo[]): ColumnDef<any>[] => {
  const orderedColumns: ColumnDef<any>[] = [];
  for (const fieldName of CHECKIN_DISPLAY_COLUMNS) {
    const apiCol = apiColumns.find(col => col.name === fieldName);
    if (apiCol) {
      const colDef: ColumnDef<any> = {
        accessorKey: apiCol.name,
        header: makeSortableHeader(CHECKIN_COLUMN_ALIASES[apiCol.name] || apiCol.label),
        enableSorting: apiCol.sortable,
        size: fieldName === 'employee_name' ? 200 : fieldName === 'email' ? 250 : 120,
      };
      if (fieldName === 'employee_name') {
        colDef.cell = ({ getValue }) => (
          <span className="text-gray-900 font-medium">{getValue() as string}</span>
        );
      } else if (['total_checkins', 'total_cycles', 'avg_checkins_per_cycle'].includes(fieldName)) {
        colDef.cell = ({ getValue }) => (
          <div className="text-center w-full">
            <span className="text-gray-900 font-medium">{String(getValue() ?? 0)}</span>
          </div>
        );
      }
      orderedColumns.push(colDef);
    }
  }
  return orderedColumns;
};

// Build DataGrid columns for review drill-down (from static config, not API)
const buildReviewColumns = (): ColumnDef<any>[] => {
  return REVIEW_DISPLAY_COLUMNS.map(fieldName => {
    const colDef: ColumnDef<any> = {
      accessorKey: fieldName,
      header: makeSortableHeader(REVIEW_COLUMN_ALIASES[fieldName] || fieldName),
      enableSorting: true,
      size: fieldName === 'email' ? 220 : fieldName === 'employeeName' || fieldName === 'manager' ? 180 : 130,
    };
    if (fieldName === 'employeeName') {
      colDef.cell = ({ getValue }) => (
        <span className="text-gray-900 font-medium">{getValue() as string || '-'}</span>
      );
    } else if (fieldName === 'dueDate' || fieldName === 'modifiedDate') {
      colDef.cell = ({ getValue }) => (
        <span className="text-gray-600">{formatDate(getValue() as string)}</span>
      );
    } else if (fieldName === 'status') {
      colDef.cell = ({ getValue }) => {
        const val = (getValue() as string) || '-';
        const color = val === 'Completed' ? 'text-emerald-600' : val === 'In Progress' ? 'text-blue-600' : 'text-gray-600';
        return <span className={`font-medium ${color}`}>{val}</span>;
      };
    } else if (fieldName === 'empStatus' || fieldName === 'mgrStatus') {
      colDef.cell = ({ getValue }) => {
        const val = (getValue() as string) || '-';
        const color = val === 'Completed' || val === 'Submitted' ? 'text-emerald-600' : val === 'In Progress' ? 'text-blue-600' : 'text-gray-500';
        return <span className={color}>{val}</span>;
      };
    }
    return colDef;
  });
};

// Time range options for the filter
const TIME_RANGE_OPTIONS = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '1y', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
] as const;

const PerformanceManagement = () => {
  const navigate = useNavigate();
  const { setTabsRightSlot } = useReportsLayoutContext();
  const { isModuleEnabled, isModuleActive } = useSubscriptionStore();
  const { canManageCustomers, hasModuleAccess, isSuperAdmin, isGlobalHRBP, isScopedHRBP, isExecutive, isManager, canViewAllData } = useUserAccess();

  // Module subscription checks
  const hasPSModule = isModuleEnabled('review-cloud') && isModuleActive('review-cloud');
  const hasLearnModule = isModuleEnabled('learn') && isModuleActive('learn');
  const hasBothModules = hasPSModule && hasLearnModule;

  // Module toggle — auto-select the available module, default to PS if both
  const [activeModule, setActiveModule] = useState<ModuleType>('performspark');

  // Auto-select module on mount based on subscription
  useEffect(() => {
    if (!hasPSModule && hasLearnModule) {
      setActiveModule('learn');
    } else {
      setActiveModule('performspark');
    }
  }, [hasPSModule, hasLearnModule]);

  // Inject module toggle into the Layout tabs row
  useEffect(() => {
    if (hasBothModules) {
      setTabsRightSlot(
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => { setActiveModule('performspark'); setActiveCategory('all'); setSelectedReport(null); }}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
              activeModule === 'performspark'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            PerformSpark
          </button>
          <button
            type="button"
            onClick={() => { setActiveModule('learn'); setActiveCategory('all'); setSelectedReport(null); }}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
              activeModule === 'learn'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            TraineryLearn
          </button>
        </div>
      );
    } else {
      setTabsRightSlot(null);
    }
    return () => setTabsRightSlot(null);
  }, [hasBothModules, activeModule, setTabsRightSlot]);

  // Learn generic report state
  const [learnRows, setLearnRows] = useState<any[]>([]);
  const [learnColumns, setLearnColumns] = useState<ColumnDef<any>[]>([]);
  const [learnLoading, setLearnLoading] = useState(false);
  const [learnError, setLearnError] = useState<string | null>(null);
  const [learnRowCount, setLearnRowCount] = useState(0);
  const [learnPagination, setLearnPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [learnSorting, setLearnSorting] = useState<SortingState>([]);
  const [learnSearchInput, setLearnSearchInput] = useState('');
  const [learnSearchQuery, setLearnSearchQuery] = useState('');
  const [learnTimeRange, setLearnTimeRange] = useState('all');
  const [learnCustomStartDate, setLearnCustomStartDate] = useState<string | undefined>();
  const [learnCustomEndDate, setLearnCustomEndDate] = useState<string | undefined>();
  const [learnColumnFilters, setLearnColumnFilters] = useState<ColumnFiltersState>([]);

  // Cycles data state
  const [rows, setRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<ColumnDef<any>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState(0);

  // Users data state
  const [usersRows, setUsersRows] = useState<any[]>([]);
  const [usersColumns, setUsersColumns] = useState<ColumnDef<any>[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersRowCount, setUsersRowCount] = useState(0);

  // Goals data state
  const [goalsRows, setGoalsRows] = useState<any[]>([]);
  const [goalsColumns, setGoalsColumns] = useState<ColumnDef<any>[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsError, setGoalsError] = useState<string | null>(null);
  const [goalsRowCount, setGoalsRowCount] = useState(0);

  // Goals sub-view and filter state
  const [goalSubView, setGoalSubView] = useState<GoalSubView>('progress_dashboard');
  const [goalCycleId, setGoalCycleId] = useState<string>('');
  const [goalCycleOptions, setGoalCycleOptions] = useState<{ value: string; label: string }[]>([]);

  // Goals grid state
  const [goalsPagination, setGoalsPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [goalsSorting, setGoalsSorting] = useState<SortingState>([]);
  const [goalsSearchInput, setGoalsSearchInput] = useState('');
  const [goalsSearchQuery, setGoalsSearchQuery] = useState('');

  // Cycles sub-view state
  const [cycleSubView, setCycleSubView] = useState<CycleSubView>('cycle_summary');

  // Engagement data state
  const [engagementRows, setEngagementRows] = useState<any[]>([]);
  const [engagementColumns, setEngagementColumns] = useState<ColumnDef<any>[]>([]);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [engagementError, setEngagementError] = useState<string | null>(null);
  const [engagementRowCount, setEngagementRowCount] = useState(0);

  // Engagement sub-view and grid state
  const [engagementSubView, setEngagementSubView] = useState<EngagementSubView>('survey_participation');
  const [engagementPagination, setEngagementPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [engagementSorting, setEngagementSorting] = useState<SortingState>([]);
  const [engagementSearchInput, setEngagementSearchInput] = useState('');
  const [engagementSearchQuery, setEngagementSearchQuery] = useState('');

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [timeRange, setTimeRange] = useState('30d');
  const [customStartDate, setCustomStartDate] = useState<string | undefined>();
  const [customEndDate, setCustomEndDate] = useState<string | undefined>();

  const [usersSearchQuery, setUsersSearchQuery] = useState('');
  const [usersTimeRange, setUsersTimeRange] = useState('30d');
  const [usersCustomStartDate, setUsersCustomStartDate] = useState<string | undefined>();
  const [usersCustomEndDate, setUsersCustomEndDate] = useState<string | undefined>();

  // Cycles grid state (TanStack format)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // Users grid state (TanStack format)
  const [usersPagination, setUsersPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [usersSorting, setUsersSorting] = useState<SortingState>([]);

  // Search input state (for debouncing)
  const [searchInput, setSearchInput] = useState('');
  const [usersSearchInput, setUsersSearchInput] = useState('');

  // Selected report state (null = card grid landing)
  const [selectedReport, setSelectedReport] = useState<ReportKey | null>(null);

  // View mode toggle
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Category filter for card grid ('all' or specific category)
  const [activeCategory, setActiveCategory] = useState<string>('all');

  // Card search query
  const [cardSearch, setCardSearch] = useState('');

  // Schedule sidebar state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleReportKey, setScheduleReportKey] = useState<string>('');
  const [scheduleReportName, setScheduleReportName] = useState<string>('');
  const [scheduleValue, setScheduleValue] = useState<Schedule>({
    frequency: 'daily',
    time: '09:00',
    recipients: [],
  });
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // Dynamic report types from API
  const [dynamicCards, setDynamicCards] = useState<ReportCard[]>([]);

  // Generic drill-down state (works for all report types)
  const [drillDown, setDrillDown] = useState<{ row: any; title: string; config: DrillDownConfig; cycleType?: string } | null>(null);
  const [drillDownRows, setDrillDownRows] = useState<any[]>([]);
  const [drillDownColumns, setDrillDownColumns] = useState<ColumnDef<any>[]>([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [drillDownRowCount, setDrillDownRowCount] = useState(0);
  const [drillDownPagination, setDrillDownPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [drillDownSorting, setDrillDownSorting] = useState<SortingState>([]);
  const [drillDownSearchInput, setDrillDownSearchInput] = useState('');
  const [drillDownSearchQuery, setDrillDownSearchQuery] = useState('');
  const [drillDownStatusFilter, setDrillDownStatusFilter] = useState<string>('all');
  const [drillDownColumnFilters, setDrillDownColumnFilters] = useState<ColumnFiltersState>([]);

  // Export hook
  const { exportReport, isExporting, error: exportError, clearError } = useExportReport();

  // Derive scoping type from user's role — determines whose data they see
  const learnScopingType = useMemo(() => {
    if (isSuperAdmin || isGlobalHRBP || canViewAllData) return 'global_hr';
    if (isScopedHRBP || isExecutive) return 'scoped_hr';
    if (isManager) return 'manager';
    return 'self';
  }, [isSuperAdmin, isGlobalHRBP, isScopedHRBP, isExecutive, isManager, canViewAllData]);

  // Generic Learn report fetcher — calls analytics API with any learn_* report key
  const fetchLearnReport = useCallback(async (reportKey: string) => {
    setLearnLoading(true);
    setLearnError(null);
    try {
      const orderBy = learnSorting.length > 0
        ? learnSorting.map(s => ({ field: s.id, direction: s.desc ? 'desc' : 'asc' }))
        : undefined;

      // Build time range for API
      let timeRangeConfig: any = undefined;
      if (learnTimeRange && learnTimeRange !== 'all') {
        if (learnTimeRange === 'custom') {
          if (learnCustomStartDate || learnCustomEndDate) {
            timeRangeConfig = { start: learnCustomStartDate, end: learnCustomEndDate };
          }
        } else {
          timeRangeConfig = { relative: learnTimeRange };
        }
      }

      const response = await analyticsApiClient.post('/reports/data', {
        report_key: reportKey,
        query_config: {
          limit: learnPagination.pageSize,
          offset: learnPagination.pageIndex * learnPagination.pageSize,
          order_by: orderBy,
          scoping_type: learnScopingType,
          ...(timeRangeConfig && { time_range: timeRangeConfig }),
          filters: learnSearchQuery
            ? [{ field: 'title', operator: 'like', value: `%${learnSearchQuery}%` }]
            : [],
        },
      });

      const data = response.data;
      // Build columns from API response with filter meta and proper sizing
      if (data.columns && data.columns.length > 0) {
        // Columns that should render as dropdown filters (small set of distinct values)
        const DROPDOWN_COLUMNS = new Set([
          'status', 'state', 'mode', 'type', 'course_type', 'platform', 'language', 'role',
          'assignment_category', 'attendance_status', 'enrollment_status', 'meeting_status',
          'progress_status', 'lesson_status', 'component_type', 'training_type', 'customer_type',
          'scope_type',
        ]);
        const BOOL_COLUMNS = new Set(['ilt_enabled', 'is_required', 'is_active', 'is_anonymous', 'is_closed']);
        const DATE_SUFFIXES = ['_at', '_date', '_on'];
        const RATE_KEYWORDS = ['rate', 'progress', 'percent', 'pct_'];
        const COUNT_KEYWORDS = ['count', 'total', 'enrolled', 'completed', 'not_started', 'in_progress', 'overdue', 'absent', 'days_'];
        const WIDE_KEYWORDS = ['name', 'title', 'email', 'course_list', 'notes', 'description'];

        const cols: ColumnDef<any>[] = data.columns.map((col: any) => {
          const n = col.name;
          const isDate = col.data_type === 'TIMESTAMP' || DATE_SUFFIXES.some(s => n.endsWith(s)) || n === 'due_date' || n === 'day_date' || n === 'start_date' || n === 'end_date' || n === 'rated_at';
          const isBool = BOOL_COLUMNS.has(n);
          const isDropdown = DROPDOWN_COLUMNS.has(n);
          const isRate = !isDate && !isDropdown && RATE_KEYWORDS.some(k => n.includes(k));
          const isCount = !isDate && !isDropdown && !isRate && COUNT_KEYWORDS.some(k => n.includes(k));
          const isWide = WIDE_KEYWORDS.some(k => n.includes(k));
          const isId = n.endsWith('_id') || n === 'id';

          // Determine filter variant — all text fields become dropdowns populated from data
          let filterVariant: string | undefined;
          if (isId) filterVariant = undefined;
          else if (isDate) filterVariant = 'dateRange';
          else if (isBool) filterVariant = 'boolean';
          else if (isRate || isCount) filterVariant = 'range';
          else filterVariant = 'multiSelect'; // All text/status/name fields → dropdown

          return {
            accessorKey: n,
            header: makeSortableHeader(col.label || n.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())),
            enableSorting: col.sortable !== false,
            enableResizing: true,
            enableColumnFilter: !isId,
            size: isWide ? 220 : isDate ? 160 : isCount || isRate ? 120 : 150,
            minSize: 80,
            maxSize: 400,
            meta: {
              label: col.label || n.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
              variant: filterVariant,
              ...(filterVariant === 'multiSelect' && { options: [] }),
              ...(isBool && { options: [{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }] }),
            },
            cell: isDate
              ? ({ getValue }: any) => <span className="text-gray-600">{formatDate(getValue() as string)}</span>
              : isRate
                ? ({ getValue }: any) => <div className="text-center w-full"><span className="text-gray-900 font-medium">{String(getValue() ?? 0)}%</span></div>
                : isCount
                  ? ({ getValue }: any) => <div className="text-center w-full"><span className="text-gray-900 font-medium">{String(getValue() ?? 0)}</span></div>
                  : isDropdown
                    ? ({ getValue }: any) => {
                        const val = getValue() as string;
                        if (!val) return <span className="text-gray-400">-</span>;
                        const colorMap: Record<string, string> = {
                          'Active': 'text-green-700 bg-green-50', 'active': 'text-green-700 bg-green-50', 'completed': 'text-green-700 bg-green-50', 'Completed': 'text-green-700 bg-green-50',
                          'in_progress': 'text-blue-700 bg-blue-50', 'In Progress': 'text-blue-700 bg-blue-50', 'registered': 'text-blue-700 bg-blue-50',
                          'Inactive': 'text-gray-600 bg-gray-50', 'not_started': 'text-gray-600 bg-gray-50', 'cancelled': 'text-gray-600 bg-gray-50',
                          'overdue': 'text-red-700 bg-red-50', 'absent': 'text-red-700 bg-red-50', 'Mandatory': 'text-orange-700 bg-orange-50',
                        };
                        const cls = colorMap[val] || 'text-gray-700 bg-gray-50';
                        return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{val}</span>;
                      }
                    : ({ getValue }: any) => {
                        const val = getValue();
                        if (val === null || val === undefined || val === '') return <span className="text-gray-400">-</span>;
                        return <span className="text-gray-900">{String(val)}</span>;
                      },
          };
        });

        // Populate dropdown options from actual data values
        const allRows = data.data || [];
        if (allRows.length > 0) {
          for (const col of cols) {
            const meta = (col as any).meta;
            if (meta?.variant === 'multiSelect') {
              const key = (col as any).accessorKey as string;
              const vals = [...new Set(allRows.map((r: any) => r[key]).filter((v: any) => v != null && v !== '' && v !== '-'))].sort();
              meta.options = vals.map((v: any) => ({ label: String(v), value: String(v) }));
            }
          }
        }

        setLearnColumns(cols);
      }

      const rowsWithIds = (data.data || []).map((row: any, i: number) => ({
        ...row,
        id: row.id ?? row.course_id ?? row.event_id ?? row.session_id ?? row.engagement_code ?? i,
      }));
      setLearnRows(rowsWithIds);
      setLearnRowCount(data.metadata?.total_rows ?? rowsWithIds.length);
    } catch (err) {
      console.error('Failed to fetch learn report:', err);
      setLearnError(err instanceof Error ? err.message : 'Failed to load report data');
      setLearnRows([]);
    } finally {
      setLearnLoading(false);
    }
  }, [learnPagination, learnSorting, learnSearchQuery, learnTimeRange, learnCustomStartDate, learnCustomEndDate]);

  // Build time range from UI state
  const buildTimeRange = useCallback((): TimeRange | undefined => {
    if (!timeRange) return undefined;
    if (timeRange === 'custom') {
      if (customStartDate || customEndDate) {
        return { start: customStartDate, end: customEndDate };
      }
      return undefined;
    }
    return { relative: timeRange };
  }, [timeRange, customStartDate, customEndDate]);

  // Build users time range from UI state
  const buildUsersTimeRange = useCallback((): TimeRange | undefined => {
    if (!usersTimeRange) return undefined;
    if (usersTimeRange === 'custom') {
      if (usersCustomStartDate || usersCustomEndDate) {
        return { start: usersCustomStartDate, end: usersCustomEndDate };
      }
      return undefined;
    }
    return { relative: usersTimeRange };
  }, [usersTimeRange, usersCustomStartDate, usersCustomEndDate]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const searchFields = cycleSubView === 'review_cycle_extended' ? ['cycle_name'] : ['cy_title'];
      const queryConfig = buildQueryConfig({
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortModel: sorting.map((s) => ({
          field: s.id,
          sort: s.desc ? 'desc' : 'asc',
        })),
        filters: [],
        timeRange: buildTimeRange(),
        searchQuery: searchQuery,
        searchFields,
      });

      let response;
      if (cycleSubView === 'review_cycle_extended') {
        response = await fetchReviewCycleExtended(queryConfig);
      } else {
        response = await fetchCycleSummary(queryConfig);
      }

      // Convert API columns to grid columns with aliases
      if (response.columns && response.columns.length > 0) {
        if (cycleSubView === 'review_cycle_extended') {
          setColumns(convertReviewExtendedColumns(response.columns));
        } else {
          setColumns(convertCycleColumns(response.columns));
        }
      }

      // Set rows with unique IDs
      const rowsWithIds = response.data.map((row: any, index: number) => ({
        ...row,
        id: row.id ?? row.cycle_id ?? index,
      }));
      setRows(rowsWithIds);

      // Set total row count for pagination
      setRowCount(response.metadata.total_rows);
    } catch (err) {
      console.error('Failed to fetch report data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load report data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [pagination, sorting, searchQuery, buildTimeRange, cycleSubView]);

  // Fetch users data function
  const fetchUsersData = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);

    try {
      const queryConfig = buildQueryConfig({
        page: usersPagination.pageIndex,
        pageSize: usersPagination.pageSize,
        sortModel: usersSorting.map((s) => ({
          field: s.id,
          sort: s.desc ? 'desc' : 'asc',
        })),
        filters: [],
        timeRange: buildUsersTimeRange(),
        searchQuery: usersSearchQuery,
        searchFields: ['first_name', 'last_name', 'email'],
      });

      const response = await fetchCycleUsers(queryConfig);

      // Convert API columns to grid columns
      if (response.columns && response.columns.length > 0) {
        setUsersColumns(convertUserColumns(response.columns));
      }

      // Set rows with unique IDs
      const rowsWithIds = response.data.map((row, index) => ({
        ...row,
        id: row.user_display_id ?? `user-${index}`,
      }));
      setUsersRows(rowsWithIds);

      // Set total row count for pagination
      setUsersRowCount(response.metadata.total_rows);
    } catch (err) {
      console.error('Failed to fetch users data:', err);
      setUsersError(err instanceof Error ? err.message : 'Failed to load users data');
      setUsersRows([]);
    } finally {
      setUsersLoading(false);
    }
  }, [usersPagination, usersSorting, usersSearchQuery, buildUsersTimeRange]);

  // Fetch cycles data when a cycle report is selected
  useEffect(() => {
    if (selectedReport === 'cycle_summary' || selectedReport === 'review_cycle_extended') {
      fetchData();
    }
  }, [selectedReport, fetchData]);

  // Fetch users data when users report is selected
  useEffect(() => {
    if (selectedReport === 'cycle_users') {
      fetchUsersData();
    }
  }, [selectedReport, fetchUsersData]);

  // Debounce search for cycles
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Debounce search for users
  useEffect(() => {
    const timer = setTimeout(() => {
      setUsersSearchQuery(usersSearchInput);
      setUsersPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [usersSearchInput]);

  // Handle time range change
  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleUsersTimeRangeChange = (value: string) => {
    setUsersTimeRange(value);
    setUsersPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // Handle export for Cycles tab
  const handleCyclesExport = async () => {
    clearError();
    const reportKey = cycleSubView === 'review_cycle_extended' ? 'review_cycle_extended' : 'cycle_summary';
    const exportOptions: ExportOptions = {};

    // Build time range for export
    if (timeRange === 'custom') {
      if (customStartDate || customEndDate) {
        exportOptions.time_range = { start: customStartDate, end: customEndDate };
      }
    } else if (timeRange) {
      exportOptions.time_range = { relative: timeRange };
    }

    try {
      await exportReport(reportKey, exportOptions);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // Handle export for Users tab
  const handleUsersExport = async () => {
    clearError();
    const exportOptions: ExportOptions = {};

    // Build time range for export
    if (usersTimeRange === 'custom') {
      if (usersCustomStartDate || usersCustomEndDate) {
        exportOptions.time_range = { start: usersCustomStartDate, end: usersCustomEndDate };
      }
    } else if (usersTimeRange) {
      exportOptions.time_range = { relative: usersTimeRange };
    }

    try {
      await exportReport('cycle_users', exportOptions);
    } catch (err) {
      // Error is already handled by the hook
      console.error('Export failed:', err);
    }
  };

  // Fetch goal cycle options on mount
  useEffect(() => {
    const fetchGoalCycleOptions = async () => {
      try {
        const response = await analyticsApiClient.get('/filters/goal_cycle_id');
        const options = (response.data?.options || []).map((opt: { value: string | number; label: string }) => ({
          value: String(opt.value),
          label: opt.label,
        }));
        setGoalCycleOptions(options);
      } catch (err) {
        console.error('Failed to fetch goal cycle options:', err);
      }
    };
    fetchGoalCycleOptions();
  }, []);

  // Fetch dynamic report types from API and add as cards
  useEffect(() => {
    const loadReportTypes = async () => {
      try {
        const types = await customReportsApi.fetchReportTypes('performspark');
        const newCards: ReportCard[] = types
          .filter(t => !HARDCODED_METRIC_KEYS.has(t.metricKey))
          .map(t => {
            const category = METRIC_KEY_CATEGORY_MAP[t.metricKey] || inferCategory(t.metricKey, t.label);
            const style = CATEGORY_STYLE[category] || CATEGORY_STYLE['Cycles'];
            return {
              key: `dynamic_${t.metricKey}`,
              title: t.label,
              description: t.description,
              category,
              icon: inferIcon(t.metricKey, t.label),
              iconBg: style.iconBg,
              iconColor: style.iconColor,
              gradient: style.gradient,
              badgeBg: style.badgeBg,
              badgeText: style.badgeText,
              isDynamic: true,
              metricKey: t.metricKey,
              reportTypeId: t.value,
            };
          });
        setDynamicCards(newCards);
      } catch (err) {
        console.error('Failed to fetch report types:', err);
      }
    };
    loadReportTypes();
  }, []);

  // Fetch goals data function
  const fetchGoalsData = useCallback(async () => {
    // For completion_rate and goal_details, require a cycle selection
    const needsCycle = goalSubView === 'completion_rate' || goalSubView === 'goal_details';
    if (needsCycle && !goalCycleId) {
      setGoalsRows([]);
      setGoalsColumns([]);
      setGoalsRowCount(0);
      return;
    }

    setGoalsLoading(true);
    setGoalsError(null);

    try {
      const filters = needsCycle
        ? [{ field: 'goal_cycle_id' as const, operator: '=' as const, value: goalCycleId }]
        : [];

      const searchFields = goalSubView === 'progress_dashboard'
        ? ['cycle_name']
        : goalSubView === 'goal_setting_compliance'
          ? ['employee_name']
          : goalSubView === 'completion_rate'
            ? ['employee_name']
            : ['goal_title'];

      const queryConfig = buildQueryConfig({
        page: goalsPagination.pageIndex,
        pageSize: goalsPagination.pageSize,
        sortModel: goalsSorting.map((s) => ({
          field: s.id,
          sort: s.desc ? 'desc' : 'asc',
        })),
        filters,
        searchQuery: goalsSearchQuery,
        searchFields,
      });

      let response;
      if (goalSubView === 'progress_dashboard') {
        response = await fetchGoalProgressDashboard(queryConfig);
      } else if (goalSubView === 'completion_rate') {
        response = await fetchGoalCompletionRate(queryConfig);
      } else if (goalSubView === 'goal_setting_compliance') {
        response = await fetchGoalSettingCompliance(queryConfig);
      } else {
        response = await fetchIndividualGoalSummary(queryConfig);
      }

      // Convert columns based on sub-view
      if (response.columns && response.columns.length > 0) {
        if (goalSubView === 'progress_dashboard') {
          setGoalsColumns(convertGoalProgressColumns(response.columns));
        } else if (goalSubView === 'completion_rate') {
          setGoalsColumns(convertGoalCompletionColumns(response.columns));
        } else if (goalSubView === 'goal_setting_compliance') {
          setGoalsColumns(convertGoalComplianceColumns(response.columns));
        } else {
          setGoalsColumns(convertGoalDetailsColumns(response.columns));
        }
      }

      const rowsWithIds = response.data.map((row: any, index: number) => ({
        ...row,
        id: row.goal_id ?? row.user_id ?? row.cycle_id ?? index,
      }));
      setGoalsRows(rowsWithIds);
      setGoalsRowCount(response.metadata.total_rows);
    } catch (err) {
      console.error('Failed to fetch goals data:', err);
      setGoalsError(err instanceof Error ? err.message : 'Failed to load goals data');
      setGoalsRows([]);
    } finally {
      setGoalsLoading(false);
    }
  }, [goalSubView, goalCycleId, goalsPagination, goalsSorting, goalsSearchQuery]);

  // Fetch goals data when a goal report is selected
  useEffect(() => {
    if (selectedReport === 'progress_dashboard' || selectedReport === 'completion_rate' || selectedReport === 'goal_details' || selectedReport === 'goal_setting_compliance') {
      fetchGoalsData();
    }
  }, [selectedReport, fetchGoalsData]);

  // Debounce search for goals
  useEffect(() => {
    const timer = setTimeout(() => {
      setGoalsSearchQuery(goalsSearchInput);
      setGoalsPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [goalsSearchInput]);

  const handleGoalCycleChange = (value: string) => {
    setGoalCycleId(value);
    setGoalsPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // Handle selecting a report card
  const handleSelectReport = (key: string) => {
    // Navigate to custom builder for dynamic report types
    const card = allCards.find(c => c.key === key);
    if (card?.isDynamic) {
      navigate(`/analytics/reports/configure`, { state: { preselectedType: card.reportTypeId } });
      return;
    }

    setSelectedReport(key as ReportKey);
    if (key === 'cycle_summary' || key === 'review_cycle_extended') {
      setCycleSubView(key);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setSearchInput('');
      setSearchQuery('');
    } else if (key === 'cycle_users') {
      setUsersPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setUsersSearchInput('');
      setUsersSearchQuery('');
    } else if (key === 'progress_dashboard' || key === 'completion_rate' || key === 'goal_details' || key === 'goal_setting_compliance') {
      setGoalSubView(key);
      setGoalsPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setGoalsSearchInput('');
      setGoalsSearchQuery('');
    } else if (key === 'survey_participation' || key === 'checkin_frequency') {
      setEngagementSubView(key as EngagementSubView);
      setEngagementPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setEngagementSearchInput('');
      setEngagementSearchQuery('');
    } else if (key.startsWith('learn_')) {
      // Learn reports — reset Learn grid state
      setLearnPagination({ pageIndex: 0, pageSize: 50 });
      setLearnSearchInput('');
      setLearnSearchQuery('');
      setLearnSorting([]);
    }
  };

  // Handle going back to the card grid
  const handleBackToCards = () => {
    setSelectedReport(null);
    setDrillDown(null);
  };

  // Open schedule sidebar for a report
  const handleOpenSchedule = (reportKey: string, reportName: string) => {
    setScheduleReportKey(reportKey);
    setScheduleReportName(reportName);
    setScheduleValue({ frequency: 'daily', time: '09:00', recipients: [] });
    setScheduleOpen(true);
  };

  // Save schedule — creates a custom report entry with the schedule
  const handleSaveSchedule = async () => {
    if (!scheduleReportKey || scheduleValue.recipients.length === 0) return;
    setScheduleSaving(true);
    try {
      // Find the report_type ID from the API using the correct module
      const mod = scheduleReportKey.startsWith('learn_') ? 'learn' : 'performspark';
      const types = await customReportsApi.fetchReportTypes(mod);
      const reportType = types.find((t) => t.metricKey === scheduleReportKey);
      if (!reportType) {
        console.error('Report type not found for key:', scheduleReportKey, 'in module:', mod);
        setScheduleSaving(false);
        return;
      }

      // Fetch the report schema to get default columns
      const schema = await customReportsApi.fetchReportSchema(scheduleReportKey);
      const columns = customReportsApi.transformSchemaColumns(schema);

      // Create the scheduled report via the custom reports API
      await customReportsApi.createReport({
        name: `${scheduleReportName} (Scheduled)`,
        description: `Scheduled delivery of ${scheduleReportName}`,
        module: mod,
        type: reportType.value,
        dateRange: { type: '30d', startDate: '', endDate: '' },
        columns,
        schedule: scheduleValue,
        status: 'active',
      });

      setScheduleOpen(false);
    } catch (err) {
      console.error('Failed to save schedule:', err);
    } finally {
      setScheduleSaving(false);
    }
  };

  // Generic row click handler — looks up drill-down config for the current report
  const handleRowDrillDown = useCallback((row: any) => {
    if (!selectedReport) return;
    const config = DRILL_DOWN_MAP[selectedReport];
    if (!config) return;

    setDrillDown({
      row,
      title: config.getTitle(row),
      config,
      cycleType: row.cy_type || row.cycle_type || '',
    });
    setDrillDownPagination({ pageIndex: 0, pageSize: 50 });
    setDrillDownSearchInput('');
    setDrillDownSearchQuery('');
    setDrillDownStatusFilter('all');
    setDrillDownSorting([]);
    setDrillDownColumnFilters([]);
  }, [selectedReport]);

  // Handle going back from drill-down to summary
  const handleBackFromDrillDown = () => {
    setDrillDown(null);
    setDrillDownRows([]);
    setDrillDownColumns([]);
    setDrillDownRowCount(0);
  };

  // Generic fetch for drill-down data (dispatches based on config type)
  const fetchDrillDownData = useCallback(async () => {
    if (!drillDown) return;
    const { config, row } = drillDown;

    setDrillDownLoading(true);
    try {
      if (config.type === 'reviewCloud') {
        // Call Review Cloud's listAllReviews API
        const token = localStorage.getItem('access_token');
        if (!token) { setDrillDownLoading(false); return; }

        const filter = config.getFilter(row);
        const sortParam = drillDownSorting.length > 0 ? drillDownSorting[0].id : 'empAssignedDate';
        const sortOrder = drillDownSorting.length > 0 ? (drillDownSorting[0].desc ? 'desc' : 'asc') : 'desc';

        // For users/checkin drill-down, the filter value is a search param (name), not a cycleId
        const isSearchBased = filter.field === 'searchParam';
        const searchValue = isSearchBased
          ? (drillDownSearchQuery || filter.value)
          : drillDownSearchQuery;

        const response = await axios.post(
          `${REVIEW_CLOUD_API_BASE_URL}/listAllReviews`,
          {
            cycleId: isSearchBased ? 0 : (filter.value as number),
            pageNumber: drillDownPagination.pageIndex,
            pageCount: drillDownPagination.pageSize,
            sortParam,
            sortOrder,
            searchParam: searchValue,
            filterStatus: drillDownStatusFilter !== 'all' ? [drillDownStatusFilter] : [],
            filterDueStartDate: '',
            filterDueEndDate: '',
            filterModifiedStartDate: '',
            filterModifiedEndDate: '',
            cycleType: drillDown.cycleType || '',
          },
          {
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            timeout: 15000,
          }
        );

        const result = response.data?.data;
        if (result?.payload) {
          const reviewsWithNames = result.payload.map((r: any, idx: number) => ({
            ...r,
            id: r.flowBoardUsersId ?? idx,
            employeeName: r.employeeName || `${r.firstName || ''} ${r.lastName || ''}`.trim() || '-',
          }));
          setDrillDownRows(reviewsWithNames);
          setDrillDownRowCount(result.count ?? result.payload.length);
        } else {
          setDrillDownRows([]);
          setDrillDownRowCount(0);
        }

        // Build columns and populate dropdown options from data
        const revCols = buildReviewColumns();
        const revRows = result?.payload || [];
        if (revRows.length > 0) {
          for (const col of revCols) {
            const meta = (col as any).meta;
            if (meta?.variant === 'multiSelect') {
              const key = (col as any).accessorKey as string;
              const vals = [...new Set(revRows.map((r: any) => r[key]).filter((v: any) => v != null && v !== '' && v !== '-'))].sort();
              meta.options = vals.map((v: any) => ({ label: String(v), value: String(v) }));
            }
          }
        }
        setDrillDownColumns(revCols);
      } else {
        // Analytics-based drill-down: call fetchReportData with detail report key + filter
        const filter = config.getFilter(row);
        const queryConfig = buildQueryConfig({
          page: drillDownPagination.pageIndex,
          pageSize: drillDownPagination.pageSize,
          sortModel: drillDownSorting.map(s => ({ field: s.id, sort: s.desc ? 'desc' : 'asc' })),
          filters: [{ field: filter.field, operator: filter.operator as any, value: filter.value }],
          searchQuery: drillDownSearchQuery,
          searchFields: config.searchFields,
        });

        const response = await fetchReportData(config.detailReportKey!, queryConfig);

        // Build columns from API response using the config's aliases
        if (response.columns && response.columns.length > 0) {
          const DD_DROPDOWN = new Set(['status', 'priority', 'progress_status', 'assignment_category', 'enrollment_status', 'attendance_status', 'meeting_status', 'course_type', 'mode', 'type', 'child_status', 'component_type']);
          const cols: ColumnDef<any>[] = [];
          for (const fieldName of config.displayColumns) {
            const apiCol = response.columns.find((c: any) => c.name === fieldName);
            if (apiCol) {
              const isDate = fieldName.includes('date') || fieldName.includes('_at') || fieldName.includes('_on');
              const isDropdown = DD_DROPDOWN.has(fieldName);
              const isRate = fieldName.includes('progress') || fieldName.includes('rate');
              const isWide = fieldName.includes('name') || fieldName.includes('title') || fieldName === 'email';
              const label = config.columnAliases[apiCol.name] || apiCol.label;

              const colDef: ColumnDef<any> = {
                accessorKey: apiCol.name,
                header: makeSortableHeader(label),
                enableSorting: apiCol.sortable,
                enableResizing: true,
                enableColumnFilter: true,
                size: isWide ? 200 : fieldName === 'email' ? 220 : isDate ? 150 : 130,
                minSize: 80,
                maxSize: 400,
                meta: {
                  label,
                  variant: isDate ? 'dateRange' : isRate ? 'range' : 'multiSelect',
                  ...(!isDate && !isRate && { options: [] }),
                },
              };
              if (isDate) {
                colDef.cell = ({ getValue }) => <span className="text-gray-600">{formatDate(getValue() as string)}</span>;
              } else if (isDropdown) {
                colDef.cell = ({ getValue }) => {
                  const val = (getValue() as string) || '-';
                  const color = val === 'Completed' || val === 'On Track' || val === 'completed' ? 'text-emerald-600' : val === 'In Progress' || val === 'At Risk' || val === 'in_progress' ? 'text-blue-600' : val === 'Behind' || val === 'Overdue' || val === 'overdue' ? 'text-red-600' : 'text-gray-600';
                  return <span className={`font-medium ${color}`}>{val}</span>;
                };
              } else if (isRate) {
                colDef.cell = ({ getValue }) => <div className="text-center"><span className="text-gray-900 font-medium">{String(getValue() ?? 0)}%</span></div>;
              } else if (isWide && fieldName !== 'email') {
                colDef.cell = ({ getValue }) => <span className="text-gray-900 font-medium">{(getValue() as string) || '-'}</span>;
              }
              cols.push(colDef);
            }
          }

          // Populate dropdown options from actual drill-down data
          const drillRows = response.data || [];
          if (drillRows.length > 0) {
            for (const col of cols) {
              const meta = (col as any).meta;
              if (meta?.variant === 'multiSelect') {
                const key = (col as any).accessorKey as string;
                const vals = [...new Set(drillRows.map((r: any) => r[key]).filter((v: any) => v != null && v !== '' && v !== '-'))].sort();
                meta.options = vals.map((v: any) => ({ label: String(v), value: String(v) }));
              }
            }
          }

          setDrillDownColumns(cols);
        }

        const rowsWithIds = response.data.map((r: any, idx: number) => ({
          ...r,
          id: r.goal_id ?? r.user_id ?? r.cycle_id ?? idx,
        }));
        setDrillDownRows(rowsWithIds);
        setDrillDownRowCount(response.metadata.total_rows);
      }
    } catch (err) {
      console.error('Failed to fetch drill-down data:', err);
      setDrillDownRows([]);
      setDrillDownRowCount(0);
    } finally {
      setDrillDownLoading(false);
    }
  }, [drillDown, drillDownPagination, drillDownSorting, drillDownSearchQuery, drillDownStatusFilter]);

  // Fetch drill-down data when state changes
  useEffect(() => {
    if (drillDown) {
      fetchDrillDownData();
    }
  }, [drillDown, fetchDrillDownData]);

  // Debounce drill-down search
  useEffect(() => {
    if (!drillDown) return;
    const timer = setTimeout(() => {
      setDrillDownSearchQuery(drillDownSearchInput);
      setDrillDownPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [drillDownSearchInput, drillDown]);

  // Fetch engagement data function
  const fetchEngagementData = useCallback(async () => {
    setEngagementLoading(true);
    setEngagementError(null);

    try {
      const searchFields = engagementSubView === 'survey_participation'
        ? ['survey_title']
        : ['employee_name'];

      const queryConfig = buildQueryConfig({
        page: engagementPagination.pageIndex,
        pageSize: engagementPagination.pageSize,
        sortModel: engagementSorting.map((s) => ({
          field: s.id,
          sort: s.desc ? 'desc' : 'asc',
        })),
        filters: [],
        searchQuery: engagementSearchQuery,
        searchFields,
      });

      let response;
      if (engagementSubView === 'survey_participation') {
        response = await fetchSurveyParticipation(queryConfig);
      } else {
        response = await fetchCheckinFrequency(queryConfig);
      }

      if (response.columns && response.columns.length > 0) {
        if (engagementSubView === 'survey_participation') {
          setEngagementColumns(convertSurveyColumns(response.columns));
        } else {
          setEngagementColumns(convertCheckinColumns(response.columns));
        }
      }

      const rowsWithIds = response.data.map((row: any, index: number) => ({
        ...row,
        id: row.survey_id ?? row.user_id ?? index,
      }));
      setEngagementRows(rowsWithIds);
      setEngagementRowCount(response.metadata.total_rows);
    } catch (err) {
      console.error('Failed to fetch engagement data:', err);
      setEngagementError(err instanceof Error ? err.message : 'Failed to load engagement data');
      setEngagementRows([]);
    } finally {
      setEngagementLoading(false);
    }
  }, [engagementSubView, engagementPagination, engagementSorting, engagementSearchQuery]);

  // Fetch engagement data when an engagement report is selected
  useEffect(() => {
    if (selectedReport === 'survey_participation' || selectedReport === 'checkin_frequency') {
      fetchEngagementData();
    }
  }, [selectedReport, fetchEngagementData]);

  // Debounce search for engagement
  useEffect(() => {
    const timer = setTimeout(() => {
      setEngagementSearchQuery(engagementSearchInput);
      setEngagementPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [engagementSearchInput]);

  // Fetch Learn report when a Learn report key is selected
  useEffect(() => {
    if (selectedReport && selectedReport.startsWith('learn_')) {
      fetchLearnReport(selectedReport);
    }
  }, [selectedReport, fetchLearnReport]);

  // Learn search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setLearnSearchQuery(learnSearchInput);
      setLearnPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [learnSearchInput]);

  // Handle export for Goals tab
  const handleGoalsExport = async () => {
    clearError();
    const reportKey = goalSubView === 'progress_dashboard'
      ? 'goal_progress_dashboard'
      : goalSubView === 'completion_rate'
        ? 'goal_completion_rate'
        : goalSubView === 'goal_setting_compliance'
          ? 'goal_setting_compliance'
          : 'individual_goal_summary';

    const exportOptions: ExportOptions = {};
    if (goalCycleId && goalSubView !== 'progress_dashboard') {
      exportOptions.filters = [{ field: 'goal_cycle_id', operator: '=', value: goalCycleId }];
    }

    try {
      await exportReport(reportKey, exportOptions);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // Handle export for Engagement tab
  const handleEngagementExport = async () => {
    clearError();
    const reportKey = engagementSubView === 'survey_participation'
      ? 'survey_participation'
      : 'checkin_frequency';

    try {
      await exportReport(reportKey, {});
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // Date range display
  const dateRangeDisplay = useMemo(
    () => getDateRangeDisplay(timeRange, customStartDate, customEndDate),
    [timeRange, customStartDate, customEndDate]
  );

  const usersDateRangeDisplay = useMemo(
    () => getDateRangeDisplay(usersTimeRange, usersCustomStartDate, usersCustomEndDate),
    [usersTimeRange, usersCustomStartDate, usersCustomEndDate]
  );

  // Combine static + dynamic cards
  const allCards = useMemo(() => {
    if (activeModule === 'learn') {
      return LEARN_REPORT_CARDS.filter(c => {
        // Customer reports: require canManageCustomers + permission
        if (c.category === 'Customers' && !canManageCustomers) return false;
        // Check entity:view permission if requiredPermission is set
        if (c.requiredPermission) {
          return hasModuleAccess(c.requiredPermission, 'view');
        }
        return true;
      });
    }
    return [...REPORT_CARDS, ...dynamicCards];
  }, [activeModule, dynamicCards, canManageCustomers, hasModuleAccess]);

  // Build category order including any new dynamic categories
  const allCategories = useMemo(() => {
    if (activeModule === 'learn') return LEARN_CATEGORY_ORDER;
    const dynamicCategories = dynamicCards
      .map(c => c.category)
      .filter(cat => !CATEGORY_ORDER.includes(cat));
    const uniqueDynamic = [...new Set(dynamicCategories)];
    return [...CATEGORY_ORDER, ...uniqueDynamic];
  }, [activeModule, dynamicCards]);

  // Category counts for filter pills
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const card of allCards) {
      counts[card.category] = (counts[card.category] || 0) + 1;
    }
    return counts;
  }, [allCards]);

  // Filtered cards based on active category + search
  const filteredCards = useMemo(() => {
    let cards = allCards;
    if (activeCategory !== 'all') {
      cards = cards.filter(c => c.category === activeCategory);
    }
    if (cardSearch.trim()) {
      const q = cardSearch.toLowerCase().trim();
      cards = cards.filter(c =>
        c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
      );
    }
    return cards;
  }, [allCards, activeCategory, cardSearch]);

  // Get active report card info
  const activeCard = selectedReport ? allCards.find(c => c.key === selectedReport) : null;

  return (
    <div className="h-full flex flex-col bg-white pt-1">
      {/* Card Grid / List Landing */}
      {selectedReport === null && (
        <div className="flex-1 overflow-hidden flex flex-col bg-gray-50/50">
          {/* Top Toolbar: Search + Report Count + View Toggle */}
          <div className="px-6 py-3 flex items-center justify-between gap-4 bg-white border-b border-gray-200 flex-shrink-0">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search reports..."
                value={cardSearch}
                onChange={e => setCardSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-56 placeholder:text-gray-400"
              />
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Report count */}
              <span className="text-sm text-gray-400">{filteredCards.length} reports</span>

              {/* View Toggle */}
              <div className="inline-flex items-center rounded-lg border border-gray-200 p-0.5 bg-white">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-gray-400 hover:text-gray-600'}`}
                  title="Grid view"
                >
                  <SquaresFour className="w-3.5 h-3.5" weight={viewMode === 'grid' ? 'fill' : 'regular'} />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-400 hover:text-gray-600'}`}
                  title="List view"
                >
                  <List className="w-3.5 h-3.5" weight={viewMode === 'list' ? 'bold' : 'regular'} />
                </button>
              </div>
            </div>
          </div>

          {/* Content: Sidebar + Reports */}
          <div className="flex-1 overflow-auto flex gap-6 px-6 py-6">
            {/* Category Sidebar */}
            <div className="w-[220px] flex-shrink-0 self-start sticky top-0">
              <div className="bg-white rounded-xl border border-gray-200 p-3">
                <div className="px-2 pt-1 pb-3">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-[1.2px]">Categories</span>
                </div>
                {/* All Reports */}
                <button
                  type="button"
                  onClick={() => setActiveCategory('all')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-[13px] transition-all mb-1 ${
                    activeCategory === 'all'
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <SquaresFour className="w-[18px] h-[18px] flex-shrink-0" weight={activeCategory === 'all' ? 'fill' : 'regular'} />
                  <span className="flex-1">All Reports</span>
                  <span className={`text-xs tabular-nums ${activeCategory === 'all' ? 'text-primary' : 'text-gray-400'}`}>{allCards.length}</span>
                </button>
                {/* Category buttons */}
                {allCategories.map(cat => {
                  const count = categoryCounts[cat] || 0;
                  if (count === 0) return null;
                  const style = CATEGORY_STYLE[cat];
                  const CatIcon = CATEGORY_ICONS[cat] || ChartBar;
                  const isActive = activeCategory === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setActiveCategory(cat)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-[13px] transition-all mb-1 ${
                        isActive
                          ? `${style?.badgeBg || 'bg-gray-50'} ${style?.iconColor || 'text-gray-900'} font-medium`
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <CatIcon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? style?.iconColor || '' : 'text-gray-400'}`} weight={isActive ? 'fill' : 'regular'} />
                      <span className="flex-1 truncate">{cat}</span>
                      <span className={`text-xs tabular-nums ${isActive ? style?.iconColor || '' : 'text-gray-400'}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Report Cards / List */}
            <div className="flex-1 min-w-0">
            {filteredCards.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <MagnifyingGlass className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-900">No reports found</p>
                <p className="text-xs text-gray-500 mt-1">Try adjusting your search or filter</p>
              </div>
            ) : viewMode === 'grid' ? (
              /* Grid View */
              <div className="space-y-8">
                {(activeCategory === 'all' ? allCategories : [activeCategory]).map(category => {
                  const cards = filteredCards.filter(c => c.category === category);
                  if (cards.length === 0) return null;
                  return (
                    <section key={category}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-1 h-5 rounded-full bg-gradient-to-b ${cards[0]?.gradient || 'from-gray-400 to-gray-500'}`} />
                        <h2 className="text-sm font-semibold text-gray-900">{category}</h2>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{cards.length}</span>
                        <div className="flex-1 h-px bg-gray-200/60" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {cards.map(card => {
                          const Icon = card.icon;
                          const hoverBorder = CATEGORY_HOVER_BORDER[card.category] || 'hover:border-gray-300';
                          return (
                            <div
                              key={card.key}
                              className={`group bg-white rounded-xl border border-gray-200 ${hoverBorder} hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:-translate-y-[1px] transition-all duration-200 ease-out text-left w-full flex flex-col`}
                            >
                              {/* Content area — clickable */}
                              <div
                                className="px-5 pt-6 pb-4 flex-1 cursor-pointer"
                                onClick={() => handleSelectReport(card.key)}
                              >
                                <div className={`w-11 h-11 rounded-[11px] flex items-center justify-center mb-4 ${card.iconBg}`}>
                                  <Icon className={`w-[22px] h-[22px] ${card.iconColor}`} weight="duotone" />
                                </div>
                                <h3 className="text-[14px] font-semibold text-gray-900 mb-1.5 group-hover:text-primary transition-colors duration-150 line-clamp-1">
                                  {card.title}
                                </h3>
                                <p className="text-[13px] text-gray-500 leading-relaxed line-clamp-2 min-h-[2.75rem]">
                                  {card.description}
                                </p>
                              </div>

                              {/* Bottom actions */}
                              <div className="px-5 pb-5 pt-0 flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSelectReport(card.key)}
                                  className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 text-[13px] font-medium rounded-lg bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-150"
                                >
                                  <Eye className="w-4 h-4" />
                                  View Report
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleOpenSchedule(card.key, card.title)}
                                  className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 text-[13px] font-medium rounded-lg bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-150"
                                >
                                  <Timer className="w-4 h-4" />
                                  Schedule
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            ) : (
              /* List View */
              <div className="space-y-6">
                {(activeCategory === 'all' ? allCategories : [activeCategory]).map(category => {
                  const cards = filteredCards.filter(c => c.category === category);
                  if (cards.length === 0) return null;
                  return (
                    <section key={category}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-1 h-5 rounded-full bg-gradient-to-b ${cards[0]?.gradient || 'from-gray-400 to-gray-500'}`} />
                        <h2 className="text-sm font-semibold text-gray-900">{category}</h2>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{cards.length}</span>
                        <div className="flex-1 h-px bg-gray-200/60" />
                      </div>
                      <div className="rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
                        {cards.map(card => {
                          const Icon = card.icon;
                          return (
                            <button
                              key={card.key}
                              type="button"
                              onClick={() => handleSelectReport(card.key)}
                              className="w-full flex items-center gap-4 px-5 py-3.5 bg-white hover:bg-white hover:shadow-[0_2px_8px_rgba(0,0,0,0.025)] transition-all duration-150 group text-left"
                            >
                              <div className={`w-[38px] h-[38px] rounded-[10px] ${card.iconBg} flex items-center justify-center flex-shrink-0`}>
                                <Icon className={`w-[18px] h-[18px] ${card.iconColor}`} weight="duotone" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 group-hover:text-primary transition-colors">{card.title}</h4>
                                <p className="text-xs text-gray-500 mt-0.5 truncate">{card.description}</p>
                              </div>
                              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-primary flex-shrink-0 transition-all duration-150 group-hover:translate-x-[3px]" />
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Back Button (all detail views — hide when drill-down is active, it has its own back) */}
      {selectedReport !== null && drillDown === null && (
        <div className="px-6 pt-3 pb-1 flex-none">
          <button
            type="button"
            onClick={handleBackToCards}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors group"
          >
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
            <span>Back to Reports</span>
          </button>
        </div>
      )}

      {/* Cycles Report Detail */}
      {(selectedReport === 'cycle_summary' || selectedReport === 'review_cycle_extended') && drillDown === null && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header Bar */}
          <div className="px-6 py-4 flex-none border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-900">{activeCard?.title || 'Cycles'}</h2>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{rowCount}</span>
              </div>

              <div className="flex items-center gap-2">
                {/* Time Range Dropdown */}
                <Select
                  value={timeRange}
                  onValueChange={handleTimeRangeChange}
                >
                  <SelectTrigger className="w-[160px] bg-white border border-gray-300 hover:border-gray-400">
                    <SelectValue placeholder="Time Range" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_RANGE_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="pl-2"
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Date Range Display (only for non-custom) */}
                {timeRange !== 'custom' && (
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700">
                    <span>{dateRangeDisplay}</span>
                  </div>
                )}

                {/* Search */}
                <div className="relative">
                  <Input
                    placeholder="Search..."
                    className="w-[200px] pl-3 pr-8 border-gray-300"
                    value={searchInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                  />
                  <MagnifyingGlass className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>

                {/* Download Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCyclesExport}
                  disabled={isExporting || loading}
                  title="Export to CSV"
                >
                  {isExporting ? (
                    <span className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                  ) : (
                    <DownloadSimple className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Custom Date Range Pickers */}
          {timeRange === 'custom' && (
            <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex-none">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Date Range:</span>
                <DatePicker
                  value={customStartDate}
                  onChange={(date: string | undefined) => {
                    setCustomStartDate(date);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                  placeholder="Start Date"
                  maxDate={customEndDate ? new Date(customEndDate) : undefined}
                />
                <span className="text-gray-400">to</span>
                <DatePicker
                  value={customEndDate}
                  onChange={(date: string | undefined) => {
                    setCustomEndDate(date);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                  placeholder="End Date"
                  minDate={customStartDate ? new Date(customStartDate) : undefined}
                />
              </div>
            </div>
          )}

          {/* Error Display */}
          {(error || exportError) && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex-none">
              <p className="text-red-800 text-sm">{error || exportError}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={error ? fetchData : clearError}
              >
                {error ? 'Retry' : 'Dismiss'}
              </Button>
            </div>
          )}

          {/* Data Grid */}
          <div className="flex-1 overflow-hidden px-6 py-4 h-full">
            <DataGrid
              data={rows}
              columns={columns}
              loading={loading}
              manualPagination={true}
              manualSorting={true}
              pageCount={Math.ceil(rowCount / pagination.pageSize)}
              totalCount={rowCount}
              pagination={pagination}
              onPaginationChange={setPagination}
              sorting={sorting}
              onSortingChange={setSorting}
              enableRowSelection={false}
              fullHeight={true}
              pageSizeOptions={[10, 20, 30, 40, 50]}
              rowDensity="normal"
              showToolbar={true}
              enableColumnResizing={true}
              onRowClick={handleRowDrillDown}
            />
          </div>
        </div>
      )}

      {/* Generic Drill-Down Detail View (works for all report types) */}
      {drillDown !== null && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header Bar */}
          <div className="px-6 py-4 flex-none border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleBackFromDrillDown}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors group"
                >
                  <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                  <span>Back to {activeCard?.title || 'Summary'}</span>
                </button>
                <div className="w-px h-5 bg-gray-200" />
                <h2 className="text-base font-semibold text-gray-900">{drillDown.title}</h2>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{drillDownRowCount}</span>
              </div>

              <div className="flex items-center gap-2">
                {/* Status Filter (only for reviewCloud type) */}
                {drillDown.config.type === 'reviewCloud' && (
                  <Select
                    value={drillDownStatusFilter}
                    onValueChange={(value: string) => {
                      setDrillDownStatusFilter(value);
                      setDrillDownPagination(prev => ({ ...prev, pageIndex: 0 }));
                    }}
                  >
                    <SelectTrigger className="w-[150px] bg-white border border-gray-300 hover:border-gray-400">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {REVIEW_STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="pl-2">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Search */}
                <div className="relative">
                  <Input
                    placeholder="Search..."
                    className="w-[200px] pl-3 pr-8 border-gray-300"
                    value={drillDownSearchInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDrillDownSearchInput(e.target.value)}
                  />
                  <MagnifyingGlass className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>

                {/* Export */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const cols = drillDown.config.displayColumns;
                    const aliases = drillDown.config.columnAliases;
                    const csvHeaders = cols.map(c => aliases[c] || c).join(',');
                    const csvData = drillDownRows.map(r =>
                      cols.map(c => {
                        const val = r[c] ?? '';
                        return `"${String(val).replace(/"/g, '""')}"`;
                      }).join(',')
                    );
                    const csv = [csvHeaders, ...csvData].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${drillDown.title.replace(/\s+/g, '_')}_detail.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  disabled={drillDownLoading || drillDownRows.length === 0}
                  title="Export to CSV"
                >
                  <DownloadSimple className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Drill-Down DataGrid */}
          <div className="flex-1 overflow-hidden px-6 py-4 h-full">
            <DataGrid
              data={drillDownRows}
              columns={drillDownColumns}
              loading={drillDownLoading}
              manualPagination={true}
              manualSorting={true}
              pageCount={Math.ceil(drillDownRowCount / drillDownPagination.pageSize)}
              totalCount={drillDownRowCount}
              pagination={drillDownPagination}
              onPaginationChange={setDrillDownPagination}
              sorting={drillDownSorting}
              onSortingChange={setDrillDownSorting}
              enableRowSelection={false}
              enableColumnResizing={true}
              fullHeight={true}
              pageSizeOptions={[10, 20, 30, 40, 50]}
              rowDensity="normal"
              showToolbar={true}
              searchColumn={drillDownColumns.length > 0 ? ((drillDownColumns.find((c: any) => {
                const key = c.accessorKey || '';
                return key.includes('Name') || key.includes('name') || key.includes('title') || key.includes('email');
              }) as any)?.accessorKey ?? undefined) : undefined}
              searchPlaceholder="Search..."
              columnFilters={drillDownColumnFilters}
              onColumnFiltersChange={setDrillDownColumnFilters}
              toolbarContent={(table: any) => (
                <DataGridCommandFilter
                  table={table}
                  tableName="drill_down_detail"
                />
              )}
            />
          </div>
        </div>
      )}

      {/* Users Report Detail */}
      {selectedReport === 'cycle_users' && drillDown === null && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header Bar */}
          <div className="px-6 py-4 flex-none border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-900">Users Summary</h2>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{usersRowCount}</span>
              </div>

              <div className="flex items-center gap-2">
                {/* Time Range Dropdown */}
                <Select
                  value={usersTimeRange}
                  onValueChange={handleUsersTimeRangeChange}
                >
                  <SelectTrigger className="w-[160px] bg-white border border-gray-300 hover:border-gray-400">
                    <SelectValue placeholder="Time Range" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_RANGE_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="pl-2"
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Date Range Display (only for non-custom) */}
                {usersTimeRange !== 'custom' && (
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700">
                    <span>{usersDateRangeDisplay}</span>
                  </div>
                )}

                {/* Search */}
                <div className="relative">
                  <Input
                    placeholder="Search..."
                    className="w-[200px] pl-3 pr-8 border-gray-300"
                    value={usersSearchInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsersSearchInput(e.target.value)}
                  />
                  <MagnifyingGlass className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>

                {/* Download Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleUsersExport}
                  disabled={isExporting || usersLoading}
                  title="Export to CSV"
                >
                  {isExporting ? (
                    <span className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                  ) : (
                    <DownloadSimple className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Custom Date Range Pickers */}
          {usersTimeRange === 'custom' && (
            <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex-none">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Date Range:</span>
                <DatePicker
                  value={usersCustomStartDate}
                  onChange={(date: string | undefined) => {
                    setUsersCustomStartDate(date);
                    setUsersPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                  placeholder="Start Date"
                  maxDate={usersCustomEndDate ? new Date(usersCustomEndDate) : undefined}
                />
                <span className="text-gray-400">to</span>
                <DatePicker
                  value={usersCustomEndDate}
                  onChange={(date: string | undefined) => {
                    setUsersCustomEndDate(date);
                    setUsersPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                  placeholder="End Date"
                  minDate={usersCustomStartDate ? new Date(usersCustomStartDate) : undefined}
                />
              </div>
            </div>
          )}

          {/* Error Display */}
          {(usersError || exportError) && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex-none">
              <p className="text-red-800 text-sm">{usersError || exportError}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={usersError ? fetchUsersData : clearError}
              >
                {usersError ? 'Retry' : 'Dismiss'}
              </Button>
            </div>
          )}

          {/* Data Grid */}
          <div className="flex-1 overflow-hidden px-6 py-4 h-full">
            <DataGrid
              data={usersRows}
              columns={usersColumns}
              loading={usersLoading}
              manualPagination={true}
              manualSorting={true}
              pageCount={Math.ceil(usersRowCount / usersPagination.pageSize)}
              totalCount={usersRowCount}
              pagination={usersPagination}
              onPaginationChange={setUsersPagination}
              sorting={usersSorting}
              onSortingChange={setUsersSorting}
              enableRowSelection={false}
              fullHeight={true}
              pageSizeOptions={[10, 20, 30, 40, 50]}
              rowDensity="normal"
              showToolbar={true}
              enableColumnResizing={true}
              onRowClick={handleRowDrillDown}
            />
          </div>
        </div>
      )}

      {/* Goals Report Detail */}
      {(selectedReport === 'progress_dashboard' || selectedReport === 'completion_rate' || selectedReport === 'goal_details' || selectedReport === 'goal_setting_compliance') && drillDown === null && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header Bar */}
          <div className="px-6 py-4 flex-none border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-900">{activeCard?.title || 'Goals'}</h2>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{goalsRowCount}</span>
              </div>

              <div className="flex items-center gap-2">
                {/* Goal Cycle Selector (only for completion_rate and goal_details) */}
                {(selectedReport === 'completion_rate' || selectedReport === 'goal_details') && (
                  <Select
                    value={goalCycleId}
                    onValueChange={handleGoalCycleChange}
                  >
                    <SelectTrigger className="w-[200px] bg-white border border-gray-300 hover:border-gray-400">
                      <SelectValue placeholder="Select Goal Cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      {goalCycleOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="pl-2"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Search */}
                <div className="relative">
                  <Input
                    placeholder="Search..."
                    className="w-[200px] pl-3 pr-8 border-gray-300"
                    value={goalsSearchInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGoalsSearchInput(e.target.value)}
                  />
                  <MagnifyingGlass className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>

                {/* Download Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleGoalsExport}
                  disabled={isExporting || goalsLoading}
                  title="Export to CSV"
                >
                  {isExporting ? (
                    <span className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                  ) : (
                    <DownloadSimple className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {(goalsError || exportError) && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex-none">
              <p className="text-red-800 text-sm">{goalsError || exportError}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={goalsError ? fetchGoalsData : clearError}
              >
                {goalsError ? 'Retry' : 'Dismiss'}
              </Button>
            </div>
          )}

          {/* Empty state when cycle not selected */}
          {(selectedReport === 'completion_rate' || selectedReport === 'goal_details') && !goalCycleId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Select a Goal Cycle to view data</p>
              </div>
            </div>
          ) : (
            /* Data Grid */
            <div className="flex-1 overflow-hidden px-6 py-4 h-full">
              <DataGrid
                data={goalsRows}
                columns={goalsColumns}
                loading={goalsLoading}
                manualPagination={true}
                manualSorting={true}
                pageCount={Math.ceil(goalsRowCount / goalsPagination.pageSize)}
                totalCount={goalsRowCount}
                pagination={goalsPagination}
                onPaginationChange={setGoalsPagination}
                sorting={goalsSorting}
                onSortingChange={setGoalsSorting}
                enableRowSelection={false}
                fullHeight={true}
                pageSizeOptions={[10, 20, 30, 40, 50]}
                rowDensity="normal"
                showToolbar={true}
              enableColumnResizing={true}
                onRowClick={DRILL_DOWN_MAP[selectedReport!] ? handleRowDrillDown : undefined}
              />
            </div>
          )}
        </div>
      )}

      {/* Engagement Report Detail */}
      {(selectedReport === 'survey_participation' || selectedReport === 'checkin_frequency') && drillDown === null && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header Bar */}
          <div className="px-6 py-4 flex-none border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-900">{activeCard?.title || 'Engagement'}</h2>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{engagementRowCount}</span>
              </div>

              <div className="flex items-center gap-2">
                {/* Search */}
                <div className="relative">
                  <Input
                    placeholder="Search..."
                    className="w-[200px] pl-3 pr-8 border-gray-300"
                    value={engagementSearchInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEngagementSearchInput(e.target.value)}
                  />
                  <MagnifyingGlass className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>

                {/* Download Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleEngagementExport}
                  disabled={isExporting || engagementLoading}
                  title="Export to CSV"
                >
                  {isExporting ? (
                    <span className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                  ) : (
                    <DownloadSimple className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {(engagementError || exportError) && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex-none">
              <p className="text-red-800 text-sm">{engagementError || exportError}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={engagementError ? fetchEngagementData : clearError}
              >
                {engagementError ? 'Retry' : 'Dismiss'}
              </Button>
            </div>
          )}

          {/* Data Grid */}
          <div className="flex-1 overflow-hidden px-6 py-4 h-full">
            <DataGrid
              data={engagementRows}
              columns={engagementColumns}
              loading={engagementLoading}
              manualPagination={true}
              manualSorting={true}
              pageCount={Math.ceil(engagementRowCount / engagementPagination.pageSize)}
              totalCount={engagementRowCount}
              pagination={engagementPagination}
              onPaginationChange={setEngagementPagination}
              sorting={engagementSorting}
              onSortingChange={setEngagementSorting}
              enableRowSelection={false}
              fullHeight={true}
              pageSizeOptions={[10, 20, 30, 40, 50]}
              rowDensity="normal"
              showToolbar={true}
              enableColumnResizing={true}
              onRowClick={DRILL_DOWN_MAP[selectedReport!] ? handleRowDrillDown : undefined}
            />
          </div>
        </div>
      )}

      {/* Learn Report Detail View (generic for all learn_* report keys) */}
      {selectedReport && selectedReport.startsWith('learn_') && drillDown === null && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header Bar */}
          <div className="px-6 py-4 flex-none border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-900">{activeCard?.title || 'Report'}</h2>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{learnRowCount}</span>
                {DRILL_DOWN_MAP[selectedReport!] && (
                  <span className="text-xs text-gray-400 ml-1">Click a row to view details</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Export */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    if (selectedReport) {
                      exportReport(selectedReport, {});
                    }
                  }}
                  disabled={isExporting || learnLoading}
                  title="Export to CSV"
                >
                  {isExporting ? (
                    <span className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                  ) : (
                    <DownloadSimple className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {learnError && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex-none">
              <p className="text-red-800 text-sm">{learnError}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => fetchLearnReport(selectedReport)}
              >
                Retry
              </Button>
            </div>
          )}

          {/* Data Grid */}
          <div className="flex-1 overflow-hidden px-6 py-4 h-full">
            <DataGrid
              data={learnRows}
              columns={learnColumns}
              loading={learnLoading}
              manualPagination={true}
              manualSorting={true}
              pageCount={Math.ceil(learnRowCount / learnPagination.pageSize)}
              totalCount={learnRowCount}
              pagination={learnPagination}
              onPaginationChange={setLearnPagination}
              sorting={learnSorting}
              onSortingChange={setLearnSorting}
              enableRowSelection={false}
              enableColumnResizing={true}
              fullHeight={true}
              pageSizeOptions={[10, 20, 30, 40, 50]}
              rowDensity="normal"
              showToolbar={true}
              searchColumn={learnColumns.length > 0 ? ((learnColumns.find((c: any) => {
                const key = c.accessorKey || '';
                return key.includes('name') || key.includes('title');
              }) as any)?.accessorKey ?? undefined) : undefined}
              searchPlaceholder="Search..."
              columnFilters={learnColumnFilters}
              onColumnFiltersChange={setLearnColumnFilters}
              toolbarContent={(table: any) => (
                <div className="flex items-center justify-between w-full gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Column Filters */}
                    <DataGridCommandFilter
                      table={table}
                      tableName={`learn_report_${selectedReport}`}
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Date Range */}
                    <Select
                      value={learnTimeRange}
                      onValueChange={(value: string) => {
                        setLearnTimeRange(value);
                        setLearnPagination(prev => ({ ...prev, pageIndex: 0 }));
                      }}
                    >
                      <SelectTrigger className="w-[140px] bg-white border border-gray-200 h-8 text-xs">
                        <SelectValue placeholder="Time Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="pl-2 text-xs">All Time</SelectItem>
                        <SelectItem value="7d" className="pl-2 text-xs">Last 7 Days</SelectItem>
                        <SelectItem value="30d" className="pl-2 text-xs">Last 30 Days</SelectItem>
                        <SelectItem value="90d" className="pl-2 text-xs">Last 90 Days</SelectItem>
                        <SelectItem value="1y" className="pl-2 text-xs">Last Year</SelectItem>
                        <SelectItem value="custom" className="pl-2 text-xs">Custom Range</SelectItem>
                      </SelectContent>
                    </Select>
                    {learnTimeRange === 'custom' && (
                      <>
                        <DatePicker
                          value={learnCustomStartDate}
                          onChange={(date: string | undefined) => {
                            setLearnCustomStartDate(date);
                            setLearnPagination(prev => ({ ...prev, pageIndex: 0 }));
                          }}
                          placeholder="Start"
                          maxDate={learnCustomEndDate ? new Date(learnCustomEndDate) : undefined}
                        />
                        <DatePicker
                          value={learnCustomEndDate}
                          onChange={(date: string | undefined) => {
                            setLearnCustomEndDate(date);
                            setLearnPagination(prev => ({ ...prev, pageIndex: 0 }));
                          }}
                          placeholder="End"
                          minDate={learnCustomStartDate ? new Date(learnCustomStartDate) : undefined}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
              onRowClick={DRILL_DOWN_MAP[selectedReport!] ? handleRowDrillDown : undefined}
            />
          </div>
        </div>
      )}
      {/* Schedule Sidebar */}
      <Sheet open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b border-gray-200">
            <SheetTitle className="text-lg font-semibold">Schedule Report</SheetTitle>
            <p className="text-sm text-gray-500 mt-1">{scheduleReportName}</p>
          </SheetHeader>

          <div className="py-6">
            <Scheduler value={scheduleValue} onChange={setScheduleValue} />
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
            <Button
              className="flex-1"
              onClick={handleSaveSchedule}
              disabled={scheduleSaving || scheduleValue.recipients.length === 0}
            >
              {scheduleSaving ? 'Saving...' : 'Save Schedule'}
            </Button>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>
              Cancel
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default PerformanceManagement;
